import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Upload, MessageSquare, CheckCircle, Download, Trash2, Edit, Users, Clock, AlertCircle, ChevronDown, FileText, Paperclip, User, X, RotateCcw, FolderOpen, Layers, Edit2, UserPlus, Share2, Link2, RefreshCw, Calendar, Send, Save } from 'lucide-react';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';
import { Avatar, Chip } from '../design/primitives';
import { statusMeta } from '../design/tokens';
import FileSelector from '../components/FileSelector';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper function to format duration as "00g:00s:00d"
const formatDuration = (totalMilliseconds) => {
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  // Format as "00g:00s:00d"
  const daysStr = String(days).padStart(2, '0');
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  
  return `${daysStr}g:${hoursStr}s:${minutesStr}d`;
};

export default function TaskDetail({ taskId: propTaskId, isDrawer = false, onModalOpen, onClose, onDataChanged }) {
  const { taskId: paramTaskId } = useParams();
  const taskId = propTaskId || paramTaskId; // Prop varsa onu kullan, yoksa URL'den al
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Refs for scrolling
  const commentsRef = useRef(null);
  const filesRef = useRef(null);
  const statusRef = useRef(null);
  const workflowRef = useRef(null);
  const detailsRef = useRef(null);
  const assignmentRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const fileSummary = useMemo(() => {
    if (files.length === 0) return '';
    const totalBytes = files.reduce((sum, f) => sum + (f.fileSize || 0), 0);
    let sizeStr = '';
    if (totalBytes > 0) {
      if (totalBytes >= 1024 * 1024 * 1024) sizeStr = (totalBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      else if (totalBytes >= 1024 * 1024) sizeStr = (totalBytes / (1024 * 1024)).toFixed(2) + ' MB';
      else if (totalBytes >= 1024) sizeStr = (totalBytes / 1024).toFixed(1) + ' KB';
      else sizeStr = totalBytes + ' B';
    }
    return `${files.length} dosya${sizeStr ? ', ' + sizeStr : ''}`;
  }, [files]);
  const [users, setUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]); // Rol bazlı filtrelenmiş kullanıcı listesi (dropdown'lar için)
  const [teams, setTeams] = useState([]); // Takımlar
  const [tags, setTags] = useState([]); // Kategoriler
  const [, setWorkflows] = useState([]); // İş akışları
  const [workflow, setWorkflow] = useState(null);
  const [project, setProject] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [highlightSection, setHighlightSection] = useState(null);
  const [highlightItemId, setHighlightItemId] = useState(null);
  const [, setRecentChanges] = useState([]);
  const [readItems, setReadItems] = useState(new Set()); // Okunmuş item ID'leri
  const [showReviewDialog, setShowReviewDialog] = useState(false); // İnceleme dialog'u
  const [reviewOptions, setReviewOptions] = useState([]); // Mevcut inceleme seçenekleri
  const [selectedReviewer, setSelectedReviewer] = useState(''); // Seçilen inceleyici
  const [reviewNote, setReviewNote] = useState(''); // Kontrol için açıklama/not
  const [showCompleteDialog, setShowCompleteDialog] = useState(false); // Görev tamamlama dialog'u (direkt atama için)
  const [showRevisionDialog, setShowRevisionDialog] = useState(false); // Revizyon dialog'u
  const [revisionReason, setRevisionReason] = useState(''); // Revizyon nedeni
  const [selectedRevisionUsers, setSelectedRevisionUsers] = useState([]); // Seçilen revizyon kullanıcıları (array)
  const [revisionFiles, setRevisionFiles] = useState([]); // Revizyon dosyaları
  const [sendingRevision, setSendingRevision] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [showRevisionFileSelector, setShowRevisionFileSelector] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditQuantityDialog, setShowEditQuantityDialog] = useState(false); // Miktar düzenleme dialogu
  const [editQuantityValue, setEditQuantityValue] = useState(''); // Sadece miktar değeri
  // Modal kaldırıldı - Kullanıcı zaten iş akışı listesinde bilgileri güncelleyebiliyor
  const [showEditStepQuantityDialog, setShowEditStepQuantityDialog] = useState(false); // Adım miktar düzenleme modal'ı
  const [editStepQuantityData, setEditStepQuantityData] = useState({
    stepIndex: null,
    assignmentIndex: null,
    tagId: '',
    quantityType: 'adet',
    quantityValue: ''
  });
  
  // Direkt atama görevleri için adım listesi
  const [stepQuantities, setStepQuantities] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [sharingUserId, setSharingUserId] = useState(null);
  const [shareNote, setShareNote] = useState('');
  const [editData, setEditData] = useState({ 
    title: '', 
    description: '', 
    priority: 'medium',
    quantityType: 'adet',
    quantityValue: '0',
    tagId: '', // Kategori ID
    workflowId: '' // İş akışı ID
  });
  const [, setCurrentTime] = useState(new Date()); // Canlı süre güncellemesi için
  
  // Atama değişikliği state'leri
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignData, setReassignData] = useState({
    newUserId: '',
    reason: '',
    assignmentIndex: null, // Hangi assignment değiştiriliyor
    oldUserId: null, // Eski kullanıcı ID
    oldUserName: null, // Eski kullanıcı adı
    stepIndex: null // Hangi adımın değiştirildiği (workflow için kritik!)
  });
  const [reassigning, setReassigning] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);

  // Onay modalları
  const [confirmAction, setConfirmAction] = useState(null); // { type, title, message, onConfirm }

  // Admin/Proje yöneticisi durum ve öncelik değiştirme.
  // In a workspace, isAdmin promotes to workspace admin (owner/admin/super_admin).
  const isAdmin = activeWorkspaceId
    ? isWorkspaceAdmin
    : (user?.role === 'admin' || user?.role === 'super_admin');
  const canChangePriority = isAdmin || user?.role === 'proje_yoneticisi';

  const handleStatusChange = async (newStatus) => {
    setShowStatusDropdown(false);
    if (newStatus === task.status) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { status: newStatus });
      toast.success(`Durum güncellendi: ${statusLabels[newStatus]}`);
      fetchTask();
      if (onDataChanged) onDataChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Durum güncellenemedi');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    setShowPriorityDropdown(false);
    if (newPriority === task.priority) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { priority: newPriority });
      toast.success(`Öncelik güncellendi: ${priorityLabels[newPriority]}`);
      fetchTask();
      if (onDataChanged) onDataChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Öncelik güncellenemedi');
    }
  };

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    if (!showStatusDropdown && !showPriorityDropdown) return;
    const handleClickOutside = (e) => {
      if (showStatusDropdown && !e.target.closest('[data-status-dropdown]')) {
        setShowStatusDropdown(false);
      }
      if (showPriorityDropdown && !e.target.closest('[data-priority-dropdown]')) {
        setShowPriorityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusDropdown, showPriorityDropdown]);

  // Paylaşım fonksiyonları
  const handleOpenShareModal = async () => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/share-users`);
      setShareUsers(response.data);
      setShareSearchQuery('');
      setSharingUserId(null);
      setShareNote('');
      setShowShareModal(true);
    } catch (error) {
      toast.error('Kullanıcı listesi yüklenemedi');
    }
  };

  const handleShareTask = async (targetUserId) => {
    if (!shareNote.trim()) {
      toast.error('Paylaşım notu zorunludur');
      return;
    }
    setSharingUserId(targetUserId);
    try {
      await axios.post(`${API}/tasks/${taskId}/share`, { userId: targetUserId, note: shareNote.trim() });
      const targetUser = shareUsers.find(u => u.id === targetUserId);
      toast.success(`Görev ${targetUser?.name || ''} ile paylaşıldı`);
      setShowShareModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Paylaşım başarısız');
    } finally {
      setSharingUserId(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Önceki istekleri iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const loadData = async () => {
      try {
        await fetchTask(controller.signal);
        if (controller.signal.aborted) return;
        await Promise.all([
          fetchComments(controller.signal),
          fetchFiles(controller.signal),
          fetchUsers(controller.signal),
          fetchTeams(controller.signal),
          fetchTags(controller.signal),
          fetchWorkflows(controller.signal)
        ]);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        console.error('Görev verileri yüklenirken hata:', error);
      }
    };

    loadData();

    // Görev açıldığında bildirimleri okundu işaretle (3 saniye sonra)
    const timer = setTimeout(() => {
      markTaskNotificationsRead();
    }, 3000);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Canlı süre güncellemesi için her dakika state'i güncelle
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Her 60 saniyede bir güncelle
    
    return () => clearInterval(interval);
  }, []);


  // Fetch recent changes after task is loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (task) {
      fetchRecentChanges();
    }
  }, [task, comments, files]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle URL query parameters for highlighting and scrolling
  useEffect(() => {
    if (!task) return;
    
    const highlight = searchParams.get('highlight');
    const itemId = searchParams.get('id');

    if (highlight) {
      setHighlightSection(highlight);
      setHighlightItemId(itemId);
      
      // Wait for DOM to be ready, then scroll
      setTimeout(() => {
        let targetRef = null;
        
        switch (highlight) {
          case 'comments':
            targetRef = commentsRef;
            break;
          case 'files':
            targetRef = filesRef;
            break;
          case 'status':
            targetRef = statusRef;
            break;
          case 'workflow':
            targetRef = workflowRef;
            break;
          case 'details':
            targetRef = detailsRef;
            break;
          case 'assignment':
            targetRef = assignmentRef;
            break;
          default:
            break;
        }
        
        if (targetRef?.current) {
          targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Clear highlight after 10 seconds
        setTimeout(() => {
          setHighlightSection(null);
          setHighlightItemId(null);
        }, 10000);
      }, 500);
    }
  }, [task, searchParams]);

  // IntersectionObserver for marking items as read when user scrolls to them
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Item'in %50'si görünür olduğunda
    };

    const callback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const itemId = entry.target.getAttribute('data-item-id');
          const itemType = entry.target.getAttribute('data-item-type');
          
          if (itemId && !readItems.has(itemId)) {
            // Mark as read
            setReadItems(prev => new Set([...prev, itemId]));
            
            // Send to backend to mark notification as read
            markItemAsRead(itemId, itemType);
          }
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    // Observe all comment and file elements
    const commentElements = document.querySelectorAll('[data-item-type="comment"]');
    const fileElements = document.querySelectorAll('[data-item-type="file"]');
    
    commentElements.forEach(el => observer.observe(el));
    fileElements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [comments, files, readItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const markItemAsRead = async (itemId, itemType) => {
    try {
      // Mark the notification for this specific item as read
      await axios.post(`${API}/notifications/mark-item-read`, {
        taskId: taskId,
        changeId: itemId,
        changeType: itemType
      });
      console.log(`Marked ${itemType} ${itemId} as read`);
    } catch (error) {
      console.error('Failed to mark item as read:', error);
    }
  };

  // İnceleme seçeneklerini hazırla
  const prepareReviewOptions = async () => {
    try {
      // Yeni endpoint'ten hiyerarşik üst yetkilileri getir
      await fetchAvailableReviewers();
    } catch (error) {
      console.error('Failed to prepare review options:', error);
      toast.error('İnceleme seçenekleri hazırlanamadı');
    }
  };

  // İnceleme için gönder - yeni endpoint'ten üst yetkilileri getir
  const fetchAvailableReviewers = async () => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/available-reviewers`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setReviewOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch reviewers:', error);
      toast.error('İnceleyiciler yüklenemedi');
    }
  };

  const handleSendForReview = async () => {
    if (!selectedReviewer) {
      toast.error('Lütfen bir inceleyici seçin');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/send-for-review`, {
        reviewerId: selectedReviewer,
        reviewNote: reviewNote || null  // İsteğe bağlı açıklama
        // reviewTagId gönderilmiyor - backend otomatik "KONTROL" kategorisini bulacak
      });
      
      toast.success(response.data.message);
      setShowReviewDialog(false);
      setShowCompleteDialog(false);  // Yeni dialog'u da kapat
      setSelectedReviewer('');
      setReviewNote('');  // Açıklama sıfırla
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev gönderilemedi');
    }
  };

  const markTaskNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-task-read/${taskId}`);
    } catch (error) {
      console.error('Mark notifications read failed:', error);
    }
  };

  const fetchRecentChanges = async () => {
    if (!task) return;
    
    console.log('🔍 Fetching recent changes for task:', taskId);
    
    try {
      // Get recent comments
      const commentsRes = await axios.get(`${API}/comments/${taskId}`);
      const allComments = commentsRes.data || [];
      console.log('📝 Comments fetched:', allComments.length);
      
      // Get recent files
      const filesRes = await axios.get(`${API}/tasks/${taskId}/files`);
      const allFiles = filesRes.data || [];
      console.log('📎 Files fetched:', allFiles.length);
      
      // Get task history
      let taskHistory = [];
      try {
        const historyRes = await axios.get(`${API}/tasks/${taskId}/history`);
        taskHistory = historyRes.data || [];
        console.log('📜 History fetched:', taskHistory.length);
      } catch (err) {
        console.log('⚠️  History not available:', err.message);
      }
      
      // Combine all changes
      const allChanges = [];
      const seenChangeIds = new Set(); // Duplicate kontrolü için
      
      // Önce history'yi ekle (en önemli ve detaylı veri)
      taskHistory.slice(0, 5).forEach(h => {
        console.log('🔍 History item changeType:', h.changeType, 'changeId:', h.changeId, 'action:', h.action);
        const uniqueKey = `${h.changeType}_${h.changeId}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...h, 
            type: 'history',
            changeType: h.changeType,
            displayText: h.action,
            createdAt: h.changedAt 
          });
        }
      });
      
      // Sonra comments'leri ekle (history'de olmayanlar)
      allComments.slice(0, 3).forEach(c => {
        const uniqueKey = `comment_${c.id}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...c, 
            type: 'comment', 
            displayText: 'Yeni yorum',
            createdAt: c.createdAt 
          });
        }
      });
      
      // Son olarak files'ları ekle (history'de olmayanlar)
      allFiles.slice(0, 3).forEach(f => {
        const uniqueKey = `file_${f.id}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...f, 
            type: 'file', 
            displayText: `Dosya eklendi: ${f.filename}`,
            createdAt: f.createdAt || f.uploadedAt 
          });
        }
      });
      
      // Sort by date (newest first) and take last 5
      allChanges.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      console.log('✅ Recent changes combined:', allChanges.slice(0, 5));
      console.log('📊 Setting recentChanges state with', allChanges.slice(0, 5).length, 'items');
      setRecentChanges(allChanges.slice(0, 5));
    } catch (error) {
      console.error('❌ Failed to fetch recent changes:', error);
    }
  };

  const fetchTask = async (signal) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}`, { signal });
      setTask(response.data);
      
      // Fetch project to get file size limit
      if (response.data.projectId) {
        const projectRes = await axios.get(`${API}/projects/${response.data.projectId}`, { signal });
        setProject(projectRes.data);
      }
      
      // Fetch workflow if exists (check for valid workflowId)
      if (response.data.workflowId && response.data.workflowId !== 'none' && response.data.workflowId !== '') {
        try {
          const workflowRes = await axios.get(`${API}/workflows`, { signal });
          const wf = workflowRes.data.find(w => w.id === response.data.workflowId);
          if (wf) {
            setWorkflow(wf);
            console.log('Workflow loaded:', wf);
          } else {
            console.log('Workflow not found for ID:', response.data.workflowId);
          }
        } catch (error) {
          console.error('Failed to fetch workflow:', error);
        }
      } else {
        console.log('No valid workflowId in task:', response.data.workflowId);
        
        // Direkt atama göreviyse stepQuantities'i yükle veya başlat
        if (!response.data.workflowId) {
          const quantities = response.data.stepQuantities || [];
          
          // Eğer boşsa, görev geçmişinden otomatik oluştur
          if (quantities.length === 0 && response.data.assignmentHistory && response.data.assignmentHistory.length > 0) {
            // Benzersiz KULLANICILAR (userId bazında, stepName'e bakmadan)
            const uniqueUsers = {};
            
            response.data.assignmentHistory.forEach((assignment) => {
              // Key sadece userId olsun - stepName farklı bile olsa aynı kullanıcı tek kayıt
              const key = assignment.userId;
              
              // Eğer bu kullanıcı daha önce eklenmemişse ekle
              if (!uniqueUsers[key] && assignment.userId) {
                uniqueUsers[key] = {
                  stepIndex: Object.keys(uniqueUsers).length,
                  stepName: assignment.stepName || (assignment.stepType === 'Kontrol' ? 'Kontrol' : 'Üretim'),
                  userId: assignment.userId,
                  tagId: assignment.completedTagId || '',
                  quantityType: assignment.completedQuantityType || 'adet',
                  quantityValue: assignment.completedQuantityValue || ''
                };
              }
            });
            
            const autoGenerated = Object.values(uniqueUsers);
            setStepQuantities(autoGenerated);
          } else {
            setStepQuantities(quantities);
          }
        }
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      toast.error('Görev yüklenemedi');
    }
  };

  const fetchComments = async (signal) => {
    try {
      const response = await axios.get(`${API}/comments/${taskId}`, { signal });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchFiles = async (signal) => {
    try {
      // Bu görevin dosyalarını al
      const taskFilesResponse = await axios.get(`${API}/tasks/${taskId}/files`, { signal });
      const taskFiles = taskFilesResponse.data;
      
      // Hafif usage endpoint ile kullanım bilgisini al
      const usageResponse = await axios.get(`${API}/tasks/${taskId}/files/usage`, { signal });
      const usageData = usageResponse.data || {};
      
      // Her dosya için kullanım bilgisini ekle
      const enrichedFiles = taskFiles.map(taskFile => {
        const usage = taskFile.s3Key ? usageData[taskFile.s3Key] : null;
        return {
          ...taskFile,
          usedInTasks: usage ? usage.tasks.filter(t => t.taskId !== taskId) : [],
          usageCount: usage ? usage.usageCount : 1
        };
      });
      
      setFiles(enrichedFiles);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      try {
        const taskFilesResponse = await axios.get(`${API}/tasks/${taskId}/files`);
        setFiles(taskFilesResponse.data);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    // Yetki kontrolü - admin, can_delete_files veya dosya sahibi (backend'de de kontrol ediliyor)
    const canDelete = isAdmin || user?.permissions?.includes('can_delete_files');
    // Dosya sahibi kontrolü backend tarafında yapılacak
    if (!canDelete) {
      // Dosya sahibi olabilir, backend karar versin - direkt gönder
    }

    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success('Dosya silindi');
      fetchFiles(); // Dosya listesini yenile
      fetchTask(); // Revizyon geçmişini de yenile
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.detail || 'Dosya silinirken bir hata oluştu');
    }
  };

  const fetchUsers = async (signal) => {
    try {
      const [allRes, availableRes] = await Promise.all([
        axios.get(`${API}/users`, { signal }),
        axios.get(`${API}/reports/available-users`, { signal })
      ]);
      setUsers(allRes.data);
      const sortedAvailable = [...availableRes.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setAvailableUsers(sortedAvailable);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeams = async (signal) => {
    try {
      const response = await axios.get(`${API}/teams`, { signal });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchTags = async (signal) => {
    try {
      const response = await axios.get(`${API}/tags`, { signal });
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchWorkflows = async (signal) => {
    try {
      const response = await axios.get(`${API}/workflows`, { signal });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    }
  };

  const handleCheckoutClick = async () => {
    // Modal kaldırıldı - Direkt tamamlama yapılıyor
    // Kullanıcı zaten iş akışı listesinde adım bilgilerini güncelleyebiliyor
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/next-step`);
      toast.success(response.data.message === "Task completed" 
        ? 'Görev tamamlandı!' 
        : 'Görev sonraki adıma taşındı');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleUpdateStepQuantity = async () => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}/assignments/${editStepQuantityData.assignmentIndex}/quantity`,
        {
          completedTagId: editStepQuantityData.tagId,
          completedQuantityType: editStepQuantityData.quantityType,
          completedQuantityValue: editStepQuantityData.quantityValue
        }
      );
      toast.success('Adım miktarı güncellendi');
      setShowEditStepQuantityDialog(false);
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleSendRevision = async (e) => {
    e.preventDefault();
    
    if (!revisionReason.trim()) {
      toast.error('Lütfen revizyon nedeni girin');
      return;
    }
    
    if (selectedRevisionUsers.length === 0) {
      toast.error('Lütfen en az bir kullanıcı seçin');
      return;
    }
    
    setSendingRevision(true);
    
    try {
      const formData = new FormData();
      formData.append('sendToUserIds', JSON.stringify(selectedRevisionUsers));
      formData.append('reason', revisionReason);
      
      // Dosyalar varsa ekle - mevcut ve yeni dosyaları ayır
      const existingFileIds = [];
      revisionFiles.forEach(file => {
        if (file.isExisting && file.existingFileId) {
          // Mevcut dosya - ID'sini ayrı gönder
          existingFileIds.push(file.existingFileId);
        } else {
          // Yeni dosya - normal upload
          formData.append('files', file);
        }
      });
      
      if (existingFileIds.length > 0) {
        formData.append('existingFileIds', JSON.stringify(existingFileIds));
      }
      
      await axios.post(`${API}/tasks/${taskId}/send-for-revision`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowRevisionDialog(false);
      setRevisionReason('');
      setSelectedRevisionUsers([]);
      setRevisionFiles([]);
      toast.success('Görev revizyona gönderildi');
      fetchTask();
      fetchRecentChanges();
    } catch (error) {
      console.error('Revizyon gönderme hatası:', error);
      toast.error(error.response?.data?.detail || 'Revizyon gönderilemedi');
    } finally {
      setSendingRevision(false);
    }
  };

  const handleResolveRevision = async () => {
    if (!task.currentRevision) {
      toast.error('Aktif revizyon bulunamadı');
      return;
    }
    
    try {
      await axios.post(`${API}/tasks/${taskId}/revisions/${task.currentRevision}/resolve`);
      toast.success('Revizyon tamamlandı');
      fetchTask();
      fetchRecentChanges();
    } catch (error) {
      console.error('Revizyon tamamlama hatası:', error);
      toast.error(error.response?.data?.detail || 'Revizyon tamamlanamadı');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/comments`, {
        taskId,
        content: newComment
      });
      setNewComment('');
      fetchComments();
      toast.success('Yorum eklendi');
    } catch (error) {
      toast.error('Yorum eklenemedi');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editingCommentContent.trim()) return;
    try {
      await axios.patch(`${API}/comments/${commentId}`, { content: editingCommentContent });
      setEditingCommentId(null);
      setEditingCommentContent('');
      fetchComments();
      toast.success('Yorum güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yorum güncellenemedi');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      fetchComments();
      toast.success('Yorum silindi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yorum silinemedi');
    }
  };

  const canEditComment = (comment) => {
    return isAdmin || user?.role === 'proje_yoneticisi' || comment.userId === user?.id;
  };


  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Get max file size (default 50MB)
    const maxFileSizeMB = project?.max_file_size_mb || 50;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    
    // Check file size
    if (file.size > maxFileSizeBytes) {
      toast.error(`Dosya boyutu (${fileSizeMB} MB) proje limitini (${maxFileSizeMB} MB) aşıyor`);
      e.target.value = ''; // Reset input
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/tasks/${taskId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success('Dosya başarıyla yüklendi');
      fetchFiles();
      setShowFileSelector(false); // Modal'ı kapat
      e.target.value = ''; // Reset input
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Dosya yüklenemedi';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAttachExistingFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file_id', file.id);
      
      await axios.post(`${API}/tasks/${taskId}/files/attach-existing`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Dosya başarıyla eklendi');
      fetchFiles();
      setShowFileSelector(false); // Modal'ı kapat
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Dosya eklenemedi';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.name : 'Bilinmeyen';
  };

  const handleEditTask = () => {
    setEditData({
      title: task.title,
      description: task.description || '',
      priority: task.priority || 'medium',
      quantityType: task.quantityType || 'adet',
      quantityValue: task.quantityValue || '0',
      tagId: task.tagId || '', // Mevcut kategori ID'si
      workflowId: task.workflowId || '' // Mevcut iş akışı ID'si
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        title: editData.title,
        description: editData.description
      });
      await fetchTask(); // Veriyi yeniden çek (await ile bekle)
      setShowEditDialog(false);
      toast.success('Görev güncellendi');
    } catch (error) {
      toast.error('Görev güncellenemedi: ' + (error.response?.data?.detail || 'Hata'));
    }
  };

  const canEdit = () => {
    if (!user || !task) return false;

    // Sadece Admin ve Proje Yöneticisi düzenleyebilir
    if (isAdmin) return true;
    if (user.role === 'proje_yoneticisi') return true;

    return false;
  };

  // Kullanıcı görev geçmişinde varsa veya şu anda atanmışsa veya üst yetkili ise miktar değerini düzenleyebilir
  const canEditQuantity = () => {
    if (!user || !task) return false;

    // Admin her zaman düzenleyebilir
    if (isAdmin) return true;

    // Proje yöneticisi bu projenin yöneticisiyse düzenleyebilir
    if (user.role === 'proje_yoneticisi' && project && project.managerIds && project.managerIds.includes(user.id)) {
      return true;
    }
    
    // Takım lideri bu görevdeki kullanıcının takım lideriyse düzenleyebilir
    if (user.role === 'takim_lideri' && task.assignedTo) {
      const assignedUser = users.find(u => u.id === task.assignedTo);
      if (assignedUser && assignedUser.teamIds && user.leadTeamIds) {
        const hasCommonTeam = assignedUser.teamIds.some(teamId => user.leadTeamIds.includes(teamId));
        if (hasCommonTeam) return true;
      }
    }
    
    // Şu anda atanmışsa
    if (task.assignedTo === user.id) return true;
    
    // Görev geçmişinde varsa
    if (task.assignmentHistory && task.assignmentHistory.length > 0) {
      const hasWorkedOnTask = task.assignmentHistory.some(h => h.userId === user.id);
      if (hasWorkedOnTask) return true;
    }
    
    return false;
  };

  const canSendRevision = () => {
    if (!user || !task) return false;
    
    // Eğer zaten revizyon modundaysa, HİÇ KİMSE (admin dahil) tekrar revizyon gönderemez
    if (task.currentRevision) {
      return false;
    }
    
    // Workflow'da en az 1 kişi görevi tamamlamış olmalı (completedAt !== null)
    // Yoksa revizyona gönderecek kimse yok
    if (task.assignmentHistory && task.assignmentHistory.length > 0) {
      const hasCompletedAssignment = task.assignmentHistory.some(h => h.completedAt !== null);
      if (!hasCompletedAssignment) {
        return false; // Henüz kimse tamamlamamış
      }
    } else {
      return false; // Hiç atama geçmişi yok
    }
    
    // admin/super_admin veya can_send_revision yetkisi olanlar gönderebilir
    if (isAdmin || user.permissions?.includes('all') || user.permissions?.includes('can_send_revision')) {
      return true;
    }
    
    // Atanan kullanıcı gönderebilir
    if (task.assignedTo === user.id) {
      return true;
    }
    
    return false;
  };

  const canResolveRevision = () => {
    if (!user || !task) return false;
    
    // Revizyon modunda değilse gösterme (currentRevision kontrolü)
    if (!task.currentRevision) return false;
    
    // admin/super_admin, can_resolve_revision yetkisi olanlar veya atanan kullanıcı tamamlayabilir
    if (isAdmin || user.permissions?.includes('all') || user.permissions?.includes('can_resolve_revision') || task.assignedTo === user.id) {
      return true;
    }
    
    return false;
  };

  const handleEditQuantity = () => {
    setEditQuantityValue(task.quantityValue || '');
    setShowEditQuantityDialog(true);
  };

  const handleSaveQuantity = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        quantityValue: editQuantityValue
      });
      setShowEditQuantityDialog(false);
      toast.success('Miktar güncellendi');
      fetchTask(); // Görevi yeniden yükle
    } catch (error) {
      console.error('Miktar güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Miktar güncellenemedi');
    }
  };

  const statusHexColors = {
    pending: '#6b7280',      // Gri (Kanban'daki beklemede rengi)
    beklemede: '#6b7280',    // Gri
    in_progress: '#3b82f6',  // Mavi (Kanban'daki işlemde rengi)
    işlemde: '#3b82f6',      // Mavi
    in_review: '#eab308',    // Sarı (Kanban'daki incelemede rengi)
    incelemede: '#eab308',   // Sarı
    revizyon_gerekli: '#f59e0b', // Turuncu (Revizyon gerekli)
    completed: '#22c55e',    // Yeşil (Kanban'daki tamamlandı rengi)
    tamamlandı: '#22c55e'    // Yeşil
  };

  const statusLabels = {
    pending: 'Beklemede',
    beklemede: 'Beklemede',
    in_progress: 'İşlemde',
    işlemde: 'İşlemde',
    in_review: 'İncelemede',
    incelemede: 'İncelemede',
    revizyon_gerekli: 'Revizyon Gerekli',
    completed: 'Tamamlandı',
    tamamlandı: 'Tamamlandı'
  };

  const priorityHexColors = {
    low: '#22c55e',      // Yeşil
    medium: '#eab308',   // Sarı
    high: '#f97316',     // Turuncu
    urgent: '#ef4444'    // Kırmızı
  };

  const priorityLabels = {
    low: 'Düşük',
    medium: 'Orta',
    high: 'Yüksek',
    urgent: 'Acil'
  };

  // Check if user can delete task
  const canDeleteTask = () => {
    if (!user || !task) return false;
    // admin/super_admin veya can_delete_tasks iznine sahip olanlar silebilir
    return isAdmin || user.permissions?.includes('all') || user.permissions?.includes('can_delete_tasks');
  };

  const handleDeleteTask = async () => {
    if (!canDeleteTask()) {
      toast.error('Bu görevi silme yetkiniz yok');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Görev silindi');
      setShowDeleteDialog(false);
      // Arka plan verisini sessizce yenile (loading state tetiklemeden)
      if (onDataChanged) onDataChanged();
      // Drawer'ı kapat
      if (isDrawer && onClose) {
        onClose();
      } else {
        navigate('/tasks');
      }
    } catch (error) {
      console.error('Delete task error:', error);
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  // Atama değişikliği fonksiyonu
  const handleReassign = async () => {
    if (!reassignData.newUserId) {
      toast.error('Lütfen yeni kullanıcı seçin');
      return;
    }

    setReassigning(true);
    try {
      const endpoint = task.workflowId 
        ? `${API}/tasks/${taskId}/reassign-step`
        : `${API}/tasks/${taskId}/reassign`;
      
      const payload = {
        newUserId: reassignData.newUserId,
        reason: reassignData.reason || null
      };
      
      // Workflow için stepIndex ekle (hangi adımın değiştirildiği bilgisi)
      if (task.workflowId && reassignData.stepIndex !== null) {
        payload.stepIndex = reassignData.stepIndex;
      }
      
      console.log('[REASSIGN] Sending request:', { endpoint, payload });
      
      const response = await axios.patch(endpoint, payload);

      toast.success(response.data.message || 'Atama başarıyla değiştirildi');
      
      // Dialog'u kapat ve form'u temizle
      setShowReassignDialog(false);
      setReassignData({ newUserId: '', reason: '', assignmentIndex: null, oldUserId: null, oldUserName: null, stepIndex: null });
      
      // Görev bilgilerini yeniden yükle
      await fetchTask();
      
    } catch (error) {
      console.error('Reassign error:', error);
      toast.error(error.response?.data?.detail || 'Atama değişikliği başarısız');
    } finally {
      setReassigning(false);
    }
  };

  // Enhanced linkify function to handle line breaks and basic formatting
  const formatComment = (text) => {
    if (!text) return '';
    
    const DOMPurify = require('dompurify');
    
    // Önce basit HTML escape (güvenlik için)
    text = text.replace(/&/g, '&amp;')
               .replace(/</g, '&lt;')
               .replace(/>/g, '&gt;')
               .replace(/\r/g, '');
    
    // 1. Satır bazlı işlemler (başlık ve madde listesi)
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      // Başlık: ## metin veya ##metin
      if (/^##\s*(.+)$/.test(line)) {
        return line.replace(/^##\s*(.+)$/, '<strong class="text-base font-bold block mt-1">$1</strong>');
      }
      // Madde listesi: - metin, -metin veya • metin
      if (/^[-•]\s*(.+)$/.test(line)) {
        return line.replace(/^[-•]\s*(.+)$/, '<span class="flex items-start gap-1"><span class="text-gray-500 select-none">•</span><span>$1</span></span>');
      }
      return line;
    });
    text = processedLines.join('<br>');
    
    // 2. URL'leri linkleştir
    const urlPattern = /(https?:\/\/[^\s<]+)/g;
    text = text.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener noreferrer" class="ox-accent-text hover:underline underline break-all">$1</a>');
    
    // 3. Inline formatting
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>');
    // DOMPurify ile sanitize et
    const clean = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
    
    return { __html: clean };
  };

  // Calculate time spent on a workflow step
  const calculateStepDuration = (stepIndex) => {
    if (!task.assignmentHistory || task.assignmentHistory.length === 0) {
      return null;
    }

    // Eğer görev henüz "işlemde" durumuna geçmediyse süre gösterme
    if (!task.firstStartedAt) {
      return null;
    }

    // İlk adım için özel mantık
    const isFirstStep = stepIndex === 0 || (stepIndex === -1 && task.assignmentHistory[0]?.stepIndex === -1);
    
    // O adıma ait tüm assignment kayıtlarını bul
    const stepAssignments = task.assignmentHistory.filter(h => h.stepIndex === stepIndex);
    
    if (stepAssignments.length === 0) {
      return null;
    }

    // Toplam süreyi hesapla
    let totalMilliseconds = 0;
    
    for (let i = 0; i < stepAssignments.length; i++) {
      const assignment = stepAssignments[i];
      
      // Başlangıç zamanı: İlk adım için firstStartedAt kullan, diğerleri için assignedAt
      let startTime;
      if (isFirstStep && i === 0) {
        // İlk adımın ilk assignment'ı için firstStartedAt kullan
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Bitiş zamanı: completedAt varsa onu kullan, yoksa şu anki zamanı kullan (devam ediyor)
      let endTime;
      if (assignment.completedAt) {
        endTime = new Date(assignment.completedAt);
      } else {
        // Hala devam ediyorsa şu anki zamanı kullan
        endTime = new Date();
      }
      
      totalMilliseconds += (endTime - startTime);
    }

    // Süreyi okunabilir formata çevir
    return formatDuration(totalMilliseconds);
  };

  // Calculate time spent for a single assignment (manuel atamalar için)
  const calculateAssignmentDuration = (assignment, isFirst = false, assignmentIndex = 0) => {
    // Başlangıç zamanı belirleme
    let startTime;
    let endTime;
    
    // Eğer completedAt varsa → Tamamlanmış atama
    if (assignment.completedAt) {
      // Başlangıç zamanı: İlk atama ve firstStartedAt varsa firstStartedAt, yoksa assignedAt
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      endTime = new Date(assignment.completedAt);
      const totalMilliseconds = endTime - startTime;
      return formatDuration(totalMilliseconds);
    }
    
    // completedAt yoksa → Henüz tamamlanmamış atama
    // Bir sonraki atama var mı kontrol et
    const nextAssignment = task.assignmentHistory && task.assignmentHistory[assignmentIndex + 1];
    
    if (nextAssignment) {
      // Bir sonraki atama varsa, bu atama tamamlanmış demektir
      // Süre: assignment.assignedAt → nextAssignment.assignedAt
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      endTime = new Date(nextAssignment.assignedAt);
      const totalMilliseconds = endTime - startTime;
      return formatDuration(totalMilliseconds);
    }
    
    // Bir sonraki atama yok → Bu son atama (aktif görev)
    // Sadece bu kullanıcıya atanmışsa göster
    if (task.assignedTo === assignment.userId) {
      // Başlangıç zamanı belirle
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Görev işlemdeyse veya incelemede → Canlı süre
      // Beklemede ise süre gösterme (firstStartedAt null olur)
      if (task.status === 'in_progress' || task.status === 'işlemde' || 
          task.status === 'in_review' || task.status === 'incelemede') {
        // Sadece firstStartedAt varsa süre göster
        if (task.firstStartedAt) {
          endTime = new Date();
          const totalMilliseconds = endTime - startTime;
          return formatDuration(totalMilliseconds);
        }
      }
    }
    
    return null;
  };

  // Calculate total time spent for a specific user (belirli bir kullanıcı için toplam süre)
  const calculateUserTotalDuration = (userId) => {
    if (!task.assignmentHistory || task.assignmentHistory.length === 0) {
      return null;
    }

    // O kullanıcıya ait tüm atamaları bul
    const userAssignments = task.assignmentHistory.filter(a => a.userId === userId);
    
    if (userAssignments.length === 0) {
      return null;
    }

    let totalMilliseconds = 0;
    
    userAssignments.forEach((assignment, index) => {
      // Bu assignment'ın assignment history içindeki global index'ini bul
      const globalIndex = task.assignmentHistory.findIndex(
        a => a.userId === assignment.userId && a.assignedAt === assignment.assignedAt
      );
      
      // Bir sonraki assignment var mı?
      const nextAssignment = task.assignmentHistory[globalIndex + 1];
      
      let startTime;
      let endTime;
      
      // İlk atama mı ve firstStartedAt var mı?
      const isFirstInHistory = globalIndex === 0;
      
      // Başlangıç zamanı
      if (isFirstInHistory && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Bitiş zamanı
      if (assignment.completedAt) {
        // completedAt varsa onu kullan
        endTime = new Date(assignment.completedAt);
      } else if (nextAssignment) {
        // Bir sonraki atama varsa, o atama zamanına kadar
        endTime = new Date(nextAssignment.assignedAt);
      } else if (task.assignedTo === assignment.userId) {
        // Şu anda bu kullanıcıya atanmışsa
        // Sadece firstStartedAt varsa (işlemde/incelemede) süreyi hesapla
        if (task.firstStartedAt && 
            (task.status === 'işlemde' || task.status === 'in_progress' ||
             task.status === 'incelemede' || task.status === 'in_review')) {
          endTime = new Date();
        } else {
          // Beklemedeyse veya firstStartedAt yoksa süreyi ekleme
          return;
        }
      } else {
        // Hiçbiri değilse süreyi ekleme
        return;
      }
      
      totalMilliseconds += (endTime - startTime);
    });

    if (totalMilliseconds === 0) {
      return null;
    }

    return formatDuration(totalMilliseconds);
  };

  if (!task) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}></div>
          <p className="ox-text-muted">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-4 w-full overflow-hidden" data-testid="task-detail-page">
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-2">
          {canDeleteTask() && (
            <button
              className="ox-btn ox-btn-danger"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 size={13} style={{ marginRight: 6 }} />
              Görevi Sil
            </button>
          )}
          <button
            data-testid="task-share-btn"
            className="ox-btn ox-btn-primary"
            onClick={handleOpenShareModal}
          >
            <Share2 size={13} style={{ marginRight: 6 }} />
            Paylaş
          </button>
          <button
            data-testid="task-copy-link-btn"
            className="ox-btn ox-btn-ghost"
            onClick={() => {
              const link = `${window.location.origin}/tasks?openTask=${taskId}`;
              navigator.clipboard.writeText(link);
              toast.success('Link kopyalandı');
            }}
            title="Paylaşım linkini kopyala"
          >
            <Link2 size={13} />
          </button>
        </div>
        {!isDrawer ? (
          <Link to="/tasks" className="ox-btn ox-btn-ghost">
            <ArrowLeft size={13} style={{ marginRight: 6 }} />
            Görevlere Dön
          </Link>
        ) : <div />}
      </div>

      <div className="flex flex-col xl:grid xl:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="xl:col-span-2 space-y-4 min-w-0">
          <div
            ref={detailsRef}
            className={`ox-surface rounded-md p-5 transition-all duration-300 ${
              highlightSection === 'details' || highlightSection === 'status' || highlightSection === 'assignment' ? 'ring-2' : ''
            }`}
            style={
              highlightSection === 'details' || highlightSection === 'status' || highlightSection === 'assignment'
                ? { boxShadow: '0 0 0 2px var(--accent)' }
                : undefined
            }
          >
            {project && (
              <div className="text-[12px] font-medium mb-2 flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
                <FolderOpen size={13} />
                {project.name}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <div className="flex-1 min-w-0">
                {task.currentRevision && (
                  <div
                    data-testid="task-revision-badge"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11.5px] font-semibold uppercase tracking-wide mb-2"
                    style={{
                      background: 'color-mix(in srgb, var(--warn) 14%, transparent)',
                      color: 'var(--warn)',
                      border: '1px solid color-mix(in srgb, var(--warn) 45%, transparent)',
                    }}
                  >
                    <RotateCcw size={12} />
                    Revizyonda
                  </div>
                )}
                <h1 className="ox-h1 mb-1 break-words">{task.title}</h1>
                {canEdit() && (
                  <button onClick={handleEditTask} className="ox-btn ox-btn-ghost mt-1" title="Görevi Düzenle">
                    <Edit size={13} style={{ marginRight: 6 }} />
                    Düzenle
                  </button>
                )}
              </div>
              {/* Durum Badge - Büyük ekranlarda görünür */}
              <div className="flex-shrink-0 hidden lg:block relative" data-status-dropdown>
                <button
                  data-testid="status-badge-desktop"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium${isAdmin ? ' cursor-pointer hover:opacity-80' : ''}`}
                  style={{
                    background: `${statusHexColors[task.status]}1a`,
                    color: statusHexColors[task.status],
                    border: `1px solid ${statusHexColors[task.status]}`,
                  }}
                  onClick={isAdmin ? () => setShowStatusDropdown(!showStatusDropdown) : undefined}
                  disabled={!isAdmin}
                >
                  <span className="truncate">{statusLabels[task.status]}</span>
                  {isAdmin && <ChevronDown size={13} className="flex-shrink-0" />}
                </button>
                {isAdmin && showStatusDropdown && (
                  <div
                    className="absolute right-0 top-full mt-1 ox-surface rounded-md py-1 z-50 min-w-[160px]"
                    style={{ boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}
                    data-testid="status-dropdown"
                  >
                    {['beklemede', 'işlemde', 'incelemede', 'tamamlandı'].map((s) => (
                      <button
                        key={s}
                        data-testid={`status-option-${s}`}
                        onClick={() => handleStatusChange(s)}
                        className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ox-row-hover ${task.status === s ? 'font-semibold' : ''}`}
                        style={task.status === s ? { background: 'var(--surface-muted)' } : undefined}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusHexColors[s] }} />
                        {statusLabels[s]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {task.description && (
              <p className="text-[13px] mb-4" style={{ color: 'var(--text-muted)' }}>{task.description}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[13px]">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {/* Öncelik Badge - Tıklanabilir (admin/proje_yoneticisi) */}
                <div className="relative" data-priority-dropdown>
                  <button
                    data-testid="priority-badge"
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11.5px] font-semibold${canChangePriority ? ' cursor-pointer hover:opacity-80' : ''}`}
                    style={{
                      background: `${priorityHexColors[task.priority]}1a`,
                      color: priorityHexColors[task.priority],
                      border: `1px solid ${priorityHexColors[task.priority]}`,
                    }}
                    onClick={canChangePriority ? () => setShowPriorityDropdown(!showPriorityDropdown) : undefined}
                    disabled={!canChangePriority}
                  >
                    <span className="truncate">Öncelik: {priorityLabels[task.priority]}</span>
                    {canChangePriority && <ChevronDown size={12} className="flex-shrink-0" />}
                  </button>
                  {canChangePriority && showPriorityDropdown && (
                    <div
                      className="absolute left-0 top-full mt-1 ox-surface rounded-md py-1 z-50 min-w-[140px]"
                      style={{ boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}
                      data-testid="priority-dropdown"
                    >
                      {['urgent', 'high', 'medium', 'low'].map((p) => (
                        <button
                          key={p}
                          data-testid={`priority-option-${p}`}
                          onClick={() => handlePriorityChange(p)}
                          className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ox-row-hover ${task.priority === p ? 'font-semibold' : ''}`}
                          style={task.priority === p ? { background: 'var(--surface-muted)' } : undefined}
                        >
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: priorityHexColors[p] }} />
                          {priorityLabels[p]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Durum Badge - Küçük ekranlarda görünür */}
                <div className="relative lg:hidden" data-status-dropdown>
                  <button
                    data-testid="status-badge-mobile"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium${isAdmin ? ' cursor-pointer hover:opacity-80' : ''}`}
                    style={{
                      background: `${statusHexColors[task.status]}1a`,
                      color: statusHexColors[task.status],
                      border: `1px solid ${statusHexColors[task.status]}`,
                    }}
                    onClick={isAdmin ? () => setShowStatusDropdown(!showStatusDropdown) : undefined}
                    disabled={!isAdmin}
                  >
                    <span className="truncate">{statusLabels[task.status]}</span>
                    {isAdmin && <ChevronDown size={13} className="flex-shrink-0" />}
                  </button>
                  {isAdmin && showStatusDropdown && (
                    <div
                      className="absolute left-0 top-full mt-1 ox-surface rounded-md py-1 z-50 min-w-[160px]"
                      style={{ boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}
                      data-testid="status-dropdown-mobile"
                    >
                      {['beklemede', 'işlemde', 'incelemede', 'tamamlandı'].map((s) => (
                        <button
                          key={s}
                          data-testid={`status-option-mobile-${s}`}
                          onClick={() => handleStatusChange(s)}
                          className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center gap-2 ox-row-hover ${task.status === s ? 'font-semibold' : ''}`}
                          style={task.status === s ? { background: 'var(--surface-muted)' } : undefined}
                        >
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: statusHexColors[s] }} />
                          {statusLabels[s]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {task.deadline && (
                  <span className="ox-text-muted text-[12px] inline-flex items-center gap-1">
                    <Clock size={12} />
                    Son Tarih: {new Date(task.deadline).toLocaleDateString('tr-TR')}
                  </span>
                )}
              </div>
              {/* Kategori Bilgisi */}
              {task.tagId && (() => {
                const taskTag = tags.find(t => t.id === task.tagId);
                return taskTag ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11.5px] font-medium"
                    style={{
                      background: `${taskTag.color}1a`,
                      color: taskTag.color,
                      border: `1px solid ${taskTag.color}`,
                    }}
                  >
                    <Layers size={12} />
                    {taskTag.name}
                  </span>
                ) : null;
              })()}
            </div>

            {/* Miktar Bilgisi */}
            {task.quantityType && task.quantityValue && (
              <div
                className="mt-4 rounded-md p-3 flex items-center justify-between gap-3"
                style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)' }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    className="flex items-center justify-center rounded-md flex-shrink-0"
                    style={{ width: 36, height: 36, background: 'var(--accent)', color: '#fff' }}
                  >
                    {task.quantityType === 'süre' ? <Clock size={18} /> : <Layers size={18} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider ox-text-muted mb-0.5">Görev Miktarı</div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[18px] font-semibold ox-tnum" style={{ color: 'var(--accent)' }}>
                        {task.quantityValue}
                      </span>
                      <span className="text-[12px] font-medium ox-text-muted">
                        {task.quantityType === 'süre' ? '(Süre)' : '(Adet)'}
                      </span>
                    </div>
                  </div>
                </div>
                {canEditQuantity() && (
                  <button
                    onClick={handleEditQuantity}
                    className="ox-btn ox-btn-primary flex-shrink-0"
                    title="Miktar Değerini Düzenle"
                  >
                    <Edit size={13} style={{ marginRight: 6 }} />
                    Düzenle
                  </button>
                )}
              </div>
            )}

            {/* Detaylar - 2x2 Grid Kompakt */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="ox-surface-muted rounded-md p-2.5 flex items-start gap-2">
                <User size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider ox-text-dim mb-0.5">Oluşturan</div>
                  <div className="text-[13px] font-medium break-words leading-tight">{getUserName(task.createdBy)}</div>
                </div>
              </div>
              {task.assignedTo && (
                <div className="ox-surface-muted rounded-md p-2.5 flex items-start gap-2">
                  <CheckCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--good)' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider ox-text-dim mb-0.5">Son Atanan</div>
                    <div className="text-[13px] font-medium break-words leading-tight">{getUserName(task.assignedTo)}</div>
                  </div>
                </div>
              )}
              <div className="ox-surface-muted rounded-md p-2.5 flex items-start gap-2">
                <Clock size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider ox-text-dim mb-0.5">Oluşturulma</div>
                  <div className="text-[13px] font-medium leading-tight">{new Date(task.createdAt).toLocaleDateString('tr-TR')}</div>
                  <div className="text-[11px] ox-text-dim mt-0.5 ox-mono">{new Date(task.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
              <div className="ox-surface-muted rounded-md p-2.5 flex items-start gap-2">
                <RotateCcw size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--warn)' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider ox-text-dim mb-0.5">Güncelleme</div>
                  <div className="text-[13px] font-medium leading-tight">{new Date(task.updatedAt).toLocaleDateString('tr-TR')}</div>
                  <div className="text-[11px] ox-text-dim mt-0.5 ox-mono">{new Date(task.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Progress */}
          {workflow && (
            <div
              ref={workflowRef}
              className="ox-surface rounded-md transition-all duration-300"
              style={
                highlightSection === 'workflow'
                  ? { boxShadow: '0 0 0 2px var(--accent)' }
                  : undefined
              }
            >
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="text-[13px] font-semibold flex items-center gap-2">
                  <Layers size={14} style={{ color: 'var(--accent)' }} />
                  İş Akışı: {workflow.name}
                </div>
              </div>
              <div className="p-4 space-y-2">
                {workflow.steps.map((step, index) => {
                  const checkoutRecord = task.checkoutHistory?.find(h => h.stepIndex === index);
                  const isTaskCompleted = task.status === 'tamamlandı' || task.status === 'completed';
                  
                  // Mevcut adım kontrolü - Revizyon durumunu da göz önünde bulundur
                  // Eğer currentStepIndex negatifse (revizyon), assignmentHistory'den son aktif adımı bul
                  let actualCurrentStepIndex = task.currentStepIndex;
                  if (task.currentStepIndex < 0) {
                    // Revizyon durumunda, assignmentHistory'deki son completedAt = null VE stepIndex >= 0 olan kaydın stepIndex'i
                    // Revizyon kayıtlarını (stepIndex < 0) atla
                    const activeAssignment = task.assignmentHistory?.find(a => !a.completedAt && a.stepIndex >= 0);
                    if (activeAssignment) {
                      actualCurrentStepIndex = activeAssignment.stepIndex;
                      
                      // DEBUG - Son test
                      if (index === 0) {
                        console.log('🔍 FINAL DEBUG:', {
                          activeAssignment,
                          actualCurrentStepIndex,
                          'index === actualCurrentStepIndex': index === actualCurrentStepIndex,
                          isTaskCompleted
                        });
                      }
                    } else {
                      console.log('⚠️ Active assignment bulunamadı!', task.assignmentHistory);
                    }
                  }
                  
                  // Mevcut adım sadece görev tamamlanmamışsa ve adım indexi actualCurrentStepIndex'e eşitse
                  const isCurrentStep = index === actualCurrentStepIndex && !isTaskCompleted;
                  
                  // DÜZELTME: Sadece gerçekten tamamlanmış adımları yeşil göster
                  // Ama mevcut adımsa tamamlanmış sayma (revizyon durumu için)
                  // assignmentHistory'de stepIndex ile eşleşen ve completedAt olan kayıtları kontrol et
                  const hasCompletedAssignment = task.assignmentHistory?.some(assignment => 
                    assignment.stepIndex === index && assignment.completedAt
                  );
                  
                  // checkoutHistory'de de kontrol et (eski görevler için)
                  const hasCheckoutRecord = checkoutRecord !== undefined;

                  // ÖNEMLİ: Mevcut adımsa tamamlanmış sayma (revizyon durumu için)
                  const isStepCompleted = !isCurrentStep && (hasCompletedAssignment || hasCheckoutRecord || (isTaskCompleted && index <= task.currentStepIndex));
                  
                  // Adıma atanan kişi/takım ismini bul
                  let assignedName = '';
                  let isManualOverride = false;
                  let actualUserId = null;
                  
                  // ÖNCELİK 1: assignmentHistory'deki bu adım için SON atamayı al
                  // (tamamlanmış veya tamamlanmamış fark etmez - en güncel atama)
                  const stepAssignments = task.assignmentHistory?.filter(a => 
                    a.stepIndex === index
                  ) || [];
                  const lastAssignment = stepAssignments.length > 0 
                    ? stepAssignments[stepAssignments.length - 1] 
                    : null;
                  
                  if (lastAssignment && lastAssignment.userId) {
                    actualUserId = lastAssignment.userId;
                    const realUser = users.find(u => u.id === actualUserId);
                    assignedName = realUser ? realUser.name : 'Bilinmeyen';
                    isManualOverride = (actualUserId !== step.assignToId);
                  } else if (task.manualOverrides && task.manualOverrides[index.toString()]) {
                    // ÖNCELİK 2: Manuel override kontrolü (henüz assignmentHistory kaydı yoksa)
                    actualUserId = task.manualOverrides[index.toString()];
                    const manualUser = users.find(u => u.id === actualUserId);
                    assignedName = manualUser ? manualUser.name : 'Bilinmeyen';
                    isManualOverride = true;
                  } else if (step.assignToType === 'user' && step.assignToId) {
                    // ÖNCELİK 3: Workflow definition'dan default atamayı göster
                    actualUserId = step.assignToId;
                    const assignedUser = users.find(u => u.id === actualUserId);
                    assignedName = assignedUser ? assignedUser.name : 'Bilinmeyen';
                  } else if (step.assignToType === 'team' && step.assignToId) {
                    // ÖNCELİK 4: Takım ataması
                    const assignedTeam = teams.find(t => t.id === step.assignToId);
                    assignedName = assignedTeam ? assignedTeam.name : 'Bilinmeyen Takım';
                  }
                  
                  const stepBg = isCurrentStep
                    ? 'var(--accent-weak)'
                    : isStepCompleted
                    ? 'var(--good-weak)'
                    : 'var(--surface-muted)';
                  const stepBorder = isCurrentStep
                    ? 'var(--accent)'
                    : isStepCompleted
                    ? 'var(--good)'
                    : 'var(--border)';
                  const circleBg = isCurrentStep
                    ? 'var(--accent)'
                    : isStepCompleted
                    ? 'var(--good)'
                    : 'var(--text-dim)';
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-md"
                      style={{ background: stepBg, border: `1px solid ${stepBorder}` }}
                    >
                      <div
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ width: 32, height: 32, background: circleBg, color: '#fff' }}
                      >
                        {isStepCompleted && !isCurrentStep ? (
                          <CheckCircle size={18} />
                        ) : (
                          <span className="font-semibold text-[13px]">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-[13px] font-medium flex-1 min-w-0 ${isCurrentStep ? '' : ''}`} style={{ color: isCurrentStep ? 'var(--accent)' : 'var(--text)' }}>{step.stepName}</p>
                          <Chip
                            color={step.stepType === 'Kontrol' ? '#6c4ca9' : 'var(--good)'}
                            bg={step.stepType === 'Kontrol' ? 'rgba(108,76,169,.12)' : 'var(--good-weak)'}
                          >
                            {step.stepType || 'Üretim'}
                          </Chip>
                          {assignedName && (
                            <Chip
                              color={isManualOverride ? 'var(--warn)' : 'var(--text-muted)'}
                              bg={isManualOverride ? 'var(--warn-weak)' : 'var(--surface-muted)'}
                            >
                              <Users size={11} />
                              {assignedName}
                              {isManualOverride && <span title="Manuel atama">✏️</span>}
                            </Chip>
                          )}

                          {/* Atamayı Değiştir Butonu - Admin ve Proje Yöneticisi, tüm tamamlanmamış adımlarda */}
                          {(isAdmin || user?.role === 'proje_yoneticisi') && !isStepCompleted && (
                            <button
                              onClick={() => {
                                // Bu adımdaki açık assignment'ı bul
                                const assignmentIdx = task.assignmentHistory?.findIndex(a =>
                                  a.stepIndex === index && !a.completedAt
                                );

                                // Aynı assignmentHistory öncelik mantığı
                                let actualUserId;
                                let actualUserName;

                                // ÖNCELİK 1: assignmentHistory'deki bu adım için son atama
                                const reassignStepAssignments = task.assignmentHistory?.filter(a =>
                                  a.stepIndex === index
                                ) || [];
                                const reassignLastAssignment = reassignStepAssignments.length > 0
                                  ? reassignStepAssignments[reassignStepAssignments.length - 1]
                                  : null;

                                if (reassignLastAssignment) {
                                  actualUserId = reassignLastAssignment.userId;
                                  const realUser = users.find(u => u.id === actualUserId);
                                  actualUserName = realUser?.name || 'Bilinmeyen';
                                } else if (task.manualOverrides && task.manualOverrides[index.toString()]) {
                                  // ÖNCELİK 2: Manuel override
                                  actualUserId = task.manualOverrides[index.toString()];
                                  const realUser = users.find(u => u.id === actualUserId);
                                  actualUserName = realUser?.name || 'Bilinmeyen';
                                } else {
                                  // ÖNCELİK 3: Workflow varsayılanı
                                  actualUserId = step.assignToId;
                                  actualUserName = assignedName || 'Bilinmeyen';
                                }

                                setReassignData({
                                  newUserId: '',
                                  reason: '',
                                  assignmentIndex: assignmentIdx >= 0 ? assignmentIdx : null,
                                  oldUserId: actualUserId,
                                  oldUserName: actualUserName,
                                  stepIndex: index
                                });
                                setShowReassignDialog(true);
                              }}
                              className="ox-btn ox-btn-ghost"
                              style={{ color: 'var(--danger)', padding: '2px 6px' }}
                              title="Atamayı Değiştir"
                            >
                              <UserPlus size={13} />
                            </button>
                          )}
                        </div>

                        {/* Kategori ve Miktar Bilgileri */}
                        {(() => {
                          const completedAssignment = task.assignmentHistory?.find(a =>
                            a.stepIndex === index && a.completedAt
                          );

                          if (completedAssignment && (completedAssignment.completedTagId || completedAssignment.completedQuantityValue)) {
                            const tag = tags.find(t => t.id === completedAssignment.completedTagId);
                            const canEditStep = isAdmin || completedAssignment.userId === user.id;

                            return (
                              <div className="flex items-center gap-2 mt-2 pt-2 flex-wrap" style={{ borderTop: '1px solid var(--border)' }}>
                                {tag && (
                                  <Chip color={tag.color} bg={`${tag.color}1a`}>
                                    <FolderOpen size={11} />
                                    {tag.name}
                                  </Chip>
                                )}
                                {completedAssignment.completedQuantityValue && (
                                  <Chip>
                                    <Layers size={11} />
                                    {completedAssignment.completedQuantityValue}
                                    {completedAssignment.completedQuantityType === 'adet' ? ' adet' : ''}
                                  </Chip>
                                )}
                                {canEditStep && (
                                  <button
                                    onClick={() => {
                                      const assignmentIndex = task.assignmentHistory?.findIndex(a =>
                                        a.stepIndex === index && a.completedAt
                                      );
                                      setEditStepQuantityData({
                                        stepIndex: index,
                                        assignmentIndex,
                                        tagId: completedAssignment.completedTagId || '',
                                        quantityType: completedAssignment.completedQuantityType || 'adet',
                                        quantityValue: completedAssignment.completedQuantityValue || ''
                                      });
                                      setShowEditStepQuantityDialog(true);
                                    }}
                                    className="ox-btn ox-btn-ghost"
                                    style={{ color: 'var(--accent)', padding: '2px 6px' }}
                                    title="Düzenle"
                                  >
                                    <Edit2 size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p className="text-[11px] ox-text-dim">
                            {isCurrentStep ? 'Mevcut adım' : isStepCompleted ? 'Tamamlandı' : 'Beklemede'}
                          </p>
                          {calculateStepDuration(index) && (
                            <Chip color="var(--accent)" bg="var(--accent-weak)" mono>
                              <Clock size={10} />
                              {calculateStepDuration(index)}
                            </Chip>
                          )}
                        </div>
                        {checkoutRecord && (
                          <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: 'var(--good)' }}>
                            <CheckCircle size={11} />
                            {checkoutRecord.userName} — {new Date(checkoutRecord.checkoutAt).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ADIM LİSTESİ - Direkt Atama Görevleri İçin */}
          {!task.workflowId && stepQuantities && stepQuantities.length > 0 && (
            <div className="ox-surface rounded-md">
              <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <Layers size={14} style={{ color: 'var(--accent)' }} />
                <div className="text-[13px] font-semibold">Adım Listesi</div>
                <div className="text-[11.5px] ox-text-muted ml-auto">{stepQuantities.length} adım</div>
              </div>
              <div className="p-4 space-y-2">
                {stepQuantities.map((step, index) => {
                  // Orijinal kullanıcıyı her zaman kullan (revizyon döngüsü adım gösterimini değiştirmemeli)
                  const actualUserId = step.userId;
                  const assignedUser = users.find(u => u.id === actualUserId);
                  const tag = tags.find(t => t.id === step.tagId);

                  // Bu kullanıcı görevin şu an atandığı kişi mi?
                  const isAssignedNow = actualUserId === task.assignedTo;

                  // Bu kullanıcının HERHANGİ BİR açık assignment'ı var mı?
                  const hasOpenAssignment = task.assignmentHistory?.some(a =>
                    a.userId === actualUserId && !a.completedAt
                  );

                  // Bu kullanıcının TÜM assignment'ları tamamlanmış mı?
                  const allAssignmentsCompleted = task.assignmentHistory?.some(a =>
                    a.userId === actualUserId && a.completedAt
                  );

                  // Bu kullanıcının son tamamlanmış assignment'ını bul (stepType için)
                  const completedAssignment = task.assignmentHistory?.find(a =>
                    a.userId === actualUserId && a.completedAt
                  );

                  // Mevcut adım: görevin atandığı kişi BU adımın orijinal kullanıcısı ise
                  const isCurrentStep = isAssignedNow && hasOpenAssignment && task.status !== 'tamamlandı';
                  const isStepCompleted = allAssignmentsCompleted && !isCurrentStep;

                  const stepBg = isCurrentStep ? 'var(--accent-weak)' : isStepCompleted ? 'var(--good-weak)' : 'var(--surface-muted)';
                  const stepBorder = isCurrentStep ? 'var(--accent)' : isStepCompleted ? 'var(--good)' : 'var(--border)';
                  const circleBg = isCurrentStep ? 'var(--accent)' : isStepCompleted ? 'var(--good)' : 'var(--text-dim)';
                  const isKontrol = step.stepName === 'Kontrol' || (completedAssignment && completedAssignment.stepType === 'Kontrol');

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-md"
                      style={{ background: stepBg, border: `1px solid ${stepBorder}` }}
                    >
                      <div
                        className="flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ width: 28, height: 28, background: circleBg, color: '#fff' }}
                      >
                        {isStepCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <span className="text-[12px] font-semibold">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[13px] font-semibold flex-1 min-w-0 truncate" style={{ color: isCurrentStep ? 'var(--accent)' : 'var(--text)' }}>
                            {step.stepName || 'Adım'}
                          </p>
                          <Chip
                            color={isKontrol ? 'var(--review)' : 'var(--good)'}
                            bg={isKontrol ? 'var(--review-weak)' : 'var(--good-weak)'}
                          >
                            {isKontrol ? 'Kontrol' : 'Üretim'}
                          </Chip>
                          {assignedUser && (
                            <Chip>
                              <Avatar u={assignedUser} size={14} imgSrc={assignedUser.picture} />
                              {assignedUser.name}
                            </Chip>
                          )}

                          {/* Atamayı Değiştir Butonu - Sadece admin, sadece tamamlanmamış adımlarda (Direkt Atama için) */}
                          {isAdmin && !isStepCompleted && (
                            <button
                              onClick={() => {
                                // Bu direkt atama adımındaki açık assignment'ı bul (stepIndex bazında)
                                const openAssignment = task.assignmentHistory?.find(a =>
                                  a.stepIndex === index && !a.completedAt
                                );
                                const assignmentIdx = task.assignmentHistory?.findIndex(a =>
                                  a.stepIndex === index && !a.completedAt
                                );

                                console.log('[REASSIGN-DEBUG-DIRECT] Step:', index, 'OpenAssignment:', openAssignment);
                                console.log('[REASSIGN-DEBUG-DIRECT] ActualUserId:', actualUserId, 'AssignedUser:', assignedUser?.name);
                                console.log('[REASSIGN-DEBUG-DIRECT] Setting stepIndex:', index);

                                setReassignData({
                                  newUserId: '',
                                  reason: '',
                                  assignmentIndex: assignmentIdx >= 0 ? assignmentIdx : null,
                                  oldUserId: actualUserId,
                                  oldUserName: assignedUser?.name || 'Bilinmeyen',
                                  stepIndex: index  // Direkt atama için de stepIndex ekle
                                });
                                setShowReassignDialog(true);
                              }}
                              className="ox-btn ox-btn-ghost"
                              style={{ color: 'var(--danger)', padding: '3px 6px' }}
                              title="Atamayı Değiştir"
                            >
                              <UserPlus size={12} />
                            </button>
                          )}
                        </div>

                        {/* Kategori ve Miktar Bilgileri */}
                        <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                          {tag && (
                            <Chip color={tag.color} bg={`${tag.color}20`}>
                              <FolderOpen size={11} />
                              {tag.name}
                            </Chip>
                          )}
                          {step.quantityValue && (
                            <Chip mono>
                              <Layers size={11} />
                              {step.quantityValue}{step.quantityType === 'adet' ? ' adet' : ''}
                            </Chip>
                          )}
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-2">
                          <p className="text-[11px] ox-text-muted">
                            {isCurrentStep
                              ? 'Mevcut adım'
                              : isStepCompleted
                              ? 'Tamamlandı'
                              : 'Beklemede'}
                          </p>
                          {(() => {
                            // Bu kullanıcının tüm assignment kayıtlarını bul ve toplam süreyi hesapla
                            const userAssignments = task.assignmentHistory?.filter(a => a.userId === step.userId) || [];
                            if (userAssignments.length === 0 || !task.firstStartedAt) return null;

                            let totalMilliseconds = 0;
                            userAssignments.forEach((assignment, idx) => {
                              let startTime;
                              if (idx === 0 && userAssignments[0].stepIndex === 0) {
                                startTime = new Date(task.firstStartedAt);
                              } else {
                                startTime = new Date(assignment.assignedAt);
                              }

                              let endTime;
                              if (assignment.completedAt) {
                                endTime = new Date(assignment.completedAt);
                              } else {
                                endTime = new Date();
                              }

                              totalMilliseconds += (endTime - startTime);
                            });

                            if (totalMilliseconds === 0) return null;

                            const duration = formatDuration(totalMilliseconds);
                            return duration ? (
                              <Chip color="var(--accent)" bg="var(--accent-weak)" mono>
                                <Clock size={11} />
                                {duration}
                              </Chip>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assignment History */}
          {task.assignmentHistory && task.assignmentHistory.length > 0 && (
            <div className="ox-surface rounded-md">
              <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <Users size={14} style={{ color: 'var(--accent)' }} />
                <div className="text-[13px] font-semibold">Görev Geçmişi</div>
                <div className="text-[11.5px] ox-text-muted ml-auto">{task.assignmentHistory.length} atama</div>
              </div>
              <div className="p-4 space-y-2">
                {task.assignmentHistory.map((assignment, index) => {
                  const assignedUser = users.find(u => u.id === assignment.userId);
                  const userName = assignedUser?.name || 'Bilinmeyen Kullanıcı';
                  const userPicture = assignedUser?.picture;
                  const isFirstAssignment = index === 0;
                  const duration = calculateAssignmentDuration(assignment, isFirstAssignment, index);
                  const userTotalDuration = calculateUserTotalDuration(assignment.userId);
                  const isKontrol = assignment.stepType === 'Kontrol';
                  const statusInfo = !assignment.completedAt && task.assignedTo === assignment.userId ? statusMeta(task.status) : null;

                  return (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-md" style={{ background: 'var(--surface-muted)', border: '1px solid var(--border)' }}>
                      <div className="flex-shrink-0">
                        {assignment.completedAt ? (
                          <div className="flex items-center justify-center rounded-full" style={{ width: 36, height: 36, background: 'var(--good-weak)', color: 'var(--good)' }}>
                            <CheckCircle size={18} />
                          </div>
                        ) : (
                          <Avatar
                            u={assignedUser || { name: userName }}
                            size={36}
                            imgSrc={userPicture ? (userPicture.startsWith('http') ? userPicture : `${window.location.origin}${userPicture}`) : undefined}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <p className="text-[13px] font-semibold truncate min-w-0">
                            {assignedUser ? assignedUser.name : 'Bilinmeyen Kullanıcı'}
                          </p>
                          {assignment.stepName && (
                            <Chip color="var(--accent)" bg="var(--accent-weak)">{assignment.stepName}</Chip>
                          )}
                          {assignment.stepType && (
                            <Chip
                              color={isKontrol ? 'var(--review)' : 'var(--good)'}
                              bg={isKontrol ? 'var(--review-weak)' : 'var(--good-weak)'}
                            >
                              {assignment.stepType}
                            </Chip>
                          )}
                          {userTotalDuration && (
                            <Chip color="var(--review)" bg="var(--review-weak)" mono className="ml-auto">
                              <Clock size={11} />
                              T: {userTotalDuration}
                            </Chip>
                          )}
                        </div>
                        <div className="space-y-1 text-[11.5px] ox-text-muted">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={11} />
                            Atandı: {new Date(assignment.assignedAt).toLocaleDateString('tr-TR')} - {new Date(assignment.assignedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </div>
                          {assignment.completedAt && (
                            <div className="flex items-center gap-1.5" style={{ color: 'var(--good)' }}>
                              <CheckCircle size={11} />
                              Tamamlandı: {new Date(assignment.completedAt).toLocaleDateString('tr-TR')} - {new Date(assignment.completedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                          )}
                          {statusInfo && (
                            <div className="flex items-center gap-1.5 font-medium" style={{ color: statusInfo.text }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: statusInfo.dot, display: 'inline-block' }} />
                              {statusInfo.label}
                            </div>
                          )}
                          {duration && (
                            <div className="flex items-center gap-1.5 font-medium" style={{ color: 'var(--review)' }}>
                              <Clock size={11} />
                              Bu atamada: {duration}
                            </div>
                          )}
                          {assignment.reviewNote && (
                            <div className="mt-2 p-2 rounded" style={{ background: 'var(--accent-weak)', borderLeft: '3px solid var(--accent)' }}>
                              <div className="text-[12px]" style={{ color: 'var(--text)' }}>
                                <span className="font-semibold" style={{ color: 'var(--accent)' }}>Açıklama:</span>{' '}
                                <span dangerouslySetInnerHTML={formatComment(assignment.reviewNote)} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-2 text-[11px] ox-text-dim" style={{ borderTop: '1px solid var(--border)' }}>
                Bu görev {task.assignmentHistory.length} kişiye atandı
              </div>
            </div>
          )}

          {/* Revizyon Geçmişi */}
          {task.revisionHistory && task.revisionHistory.length > 0 && (
            <div className="ox-surface rounded-md">
              <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
                <RefreshCw size={14} style={{ color: 'var(--warn)' }} />
                <div className="text-[13px] font-semibold">Revizyon Geçmişi</div>
                <div className="text-[11.5px] ox-text-muted ml-auto">{task.revisionCount || 0} revizyon</div>
              </div>
              <div className="p-4 space-y-3">
                {task.revisionHistory.slice().reverse().map((revision, index) => {
                  const isResolved = revision.status === 'resolved';
                  const cardBg = isResolved ? 'var(--good-weak)' : 'var(--warn-weak)';
                  const cardBorder = isResolved ? 'var(--good)' : 'var(--warn)';

                  return (
                    <div key={revision.id} className="p-3 rounded-md" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <Chip
                          color={isResolved ? 'var(--good)' : 'var(--warn)'}
                          bg={isResolved ? 'var(--good-weak)' : 'var(--warn-weak)'}
                        >
                          {isResolved ? <CheckCircle size={11} /> : <Clock size={11} />}
                          {isResolved ? 'Çözüldü' : 'Bekliyor'}
                        </Chip>
                        <span className="text-[11.5px] ox-text-muted">
                          {new Date(revision.sentAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(revision.sentAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="text-[12.5px] flex items-center gap-2">
                          <span className="font-medium ox-text-muted">Gönderen:</span>
                          <span>{revision.sentByName}</span>
                        </div>

                        <div className="text-[12.5px] flex items-start gap-2">
                          <span className="font-medium ox-text-muted flex-shrink-0">Gönderilen:</span>
                          <span>{revision.sentTo.map(u => u.userName).join(', ')}</span>
                        </div>

                        <div>
                          <div className="text-[11.5px] uppercase tracking-wider ox-text-dim mb-1">Revizyon Nedeni</div>
                          <div
                            className="text-[13px] p-2 rounded ox-surface"
                            style={{ borderColor: 'var(--border)' }}
                            dangerouslySetInnerHTML={formatComment(revision.reason)}
                          />
                        </div>

                        {revision.files && revision.files.length > 0 && (
                          <div>
                            <div className="text-[11.5px] uppercase tracking-wider ox-text-dim mb-1.5">
                              Eklenen Dosyalar ({revision.files.length})
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {revision.files.filter(file => file.id || file.fileId).map((file, idx) => (
                                <div key={idx} className="group flex items-center gap-1">
                                  <a
                                    href={`${window.location.origin}/api/files/${file.id || file.fileId}/download?token=${localStorage.getItem('session_token') || ''}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ox-btn ox-btn-ghost"
                                    title={`İndir: ${file.filename}`}
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <Paperclip size={11} style={{ marginRight: 5 }} />
                                    {file.filename}
                                  </a>
                                  {(isAdmin || user?.permissions?.includes('can_delete_files') || revision.sentBy === user?.id) && (
                                    <button
                                      data-testid={`revision-file-delete-${file.id || file.fileId}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFile(file.id || file.fileId);
                                      }}
                                      className="ox-btn ox-btn-ghost"
                                      style={{ color: 'var(--danger)', padding: '3px 5px' }}
                                      title="Dosyayı sil"
                                    >
                                      <Trash2 size={11} />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {isResolved && revision.resolvedAt && (
                          <div className="mt-2 pt-2 text-[11.5px] flex items-center gap-1.5" style={{ borderTop: '1px solid var(--good)', color: 'var(--good)' }}>
                            <CheckCircle size={11} />
                            {new Date(revision.resolvedAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(revision.resolvedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} tarihinde tamamlandı
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-2 text-[11px] ox-text-dim" style={{ borderTop: '1px solid var(--border)' }}>
                Toplam {task.revisionHistory.length} revizyon gönderildi
              </div>
            </div>
          )}

          {/* İnceleme Dialog */}
          {showReviewDialog && (
            <div className="ox-modal-backdrop" onClick={() => setShowReviewDialog(false)}>
              <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
                <div className="ox-modal-head">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <Send size={14} style={{ color: 'var(--accent)' }} />
                      Kontrol İçin Gönder
                    </div>
                    <div className="text-[11.5px] ox-text-muted mt-0.5">
                      Görevi inceleme için kime göndermek istersiniz?
                    </div>
                  </div>
                  <button className="ox-btn ox-btn-ghost" onClick={() => setShowReviewDialog(false)}>
                    <X size={14} />
                  </button>
                </div>

                <div className="ox-modal-body space-y-4">
                  <div className="ox-field">
                    <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                      <Users size={11} />
                      İnceleme Yapacak Kişi <span style={{ color: 'var(--danger)' }}>*</span>
                    </label>
                    {reviewOptions.length === 0 ? (
                      <div className="text-[12.5px] ox-text-muted text-center py-4 mt-2">İnceleme seçenekleri hazırlanıyor...</div>
                    ) : (
                      <div className="space-y-1.5 mt-2">
                        {reviewOptions.map((option) => {
                          const isSelected = selectedReviewer === option.id;
                          const roleLabel = option.role === 'takim_lideri' ? 'Takım Lideri'
                            : option.role === 'proje_yoneticisi' ? 'Proje Yöneticisi'
                            : option.role === 'super_admin' ? 'Süper Admin'
                            : option.role === 'admin' ? 'Admin' : option.role;
                          return (
                            <label
                              key={option.id}
                              className="flex items-center gap-3 p-3 rounded-md cursor-pointer"
                              style={{
                                background: isSelected ? 'var(--accent-weak)' : 'var(--surface-muted)',
                                border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                              }}
                            >
                              <input
                                type="radio"
                                name="reviewer"
                                value={option.id}
                                checked={isSelected}
                                onChange={(e) => setSelectedReviewer(e.target.value)}
                                style={{ accentColor: 'var(--accent)' }}
                              />
                              <Avatar u={option} size={24} />
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold truncate">{option.name}</div>
                                <div className="text-[11px] ox-text-muted">{roleLabel}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="ox-field">
                    <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                      <MessageSquare size={11} />
                      Kontrol Açıklaması (İsteğe Bağlı)
                    </label>
                    <textarea
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="Kontrol yapacak kişiye göndermek istediğiniz not veya açıklamayı buraya yazabilirsiniz..."
                      className="ox-input w-full mt-1.5"
                      rows={3}
                      style={{ resize: 'none' }}
                    />
                    <div className="text-[11px] ox-text-dim mt-1">
                      **kalın**  *italik*  ## Başlık  - Madde listesi
                    </div>
                  </div>
                </div>

                <div className="ox-modal-foot">
                  <button className="ox-btn" onClick={() => setShowReviewDialog(false)}>
                    <X size={13} style={{ marginRight: 6 }} />
                    İptal
                  </button>
                  <button
                    className="ox-btn ox-btn-primary"
                    onClick={handleSendForReview}
                    disabled={!selectedReviewer || reviewOptions.length === 0}
                  >
                    <Send size={13} style={{ marginRight: 6 }} />
                    Gönder
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Revizyon Dialog */}
          {/* Custom Modal for Revision - No Dialog Wrapper */}
          {showRevisionDialog && ReactDOM.createPortal(
            <div className="ox-modal-backdrop" style={{ zIndex: 10000 }} onClick={() => {
              setShowRevisionDialog(false);
              setRevisionReason('');
              setSelectedRevisionUsers([]);
              setRevisionFiles([]);
            }}>
              <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div className="ox-modal-head">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <RotateCcw size={14} style={{ color: 'var(--warn)' }} />
                      Görevi Revizyona Gönder
                    </div>
                    <div className="text-[11.5px] ox-text-muted mt-0.5">
                      Görevi önceki kullanıcılara geri gönderin ve revizyon nedeni belirtin.
                    </div>
                  </div>
                  <button
                    className="ox-btn ox-btn-ghost ml-auto"
                    onClick={() => {
                      setShowRevisionDialog(false);
                      setRevisionReason('');
                      setSelectedRevisionUsers([]);
                      setRevisionFiles([]);
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="ox-modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                  <form onSubmit={handleSendRevision} className="space-y-4">
                    <div className="ox-field">
                      <label htmlFor="revision-reason" className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                        <MessageSquare size={11} />
                        Revizyon Nedeni <span style={{ color: 'var(--danger)' }}>*</span>
                      </label>
                      <textarea
                        id="revision-reason"
                        value={revisionReason}
                        onChange={(e) => setRevisionReason(e.target.value)}
                        placeholder="Lütfen detaylı bir açıklama yapın..."
                        className="ox-input w-full mt-1.5"
                        rows={4}
                        required
                        style={{ resize: 'vertical', minHeight: 100 }}
                      />
                      <div className="text-[11px] ox-text-dim mt-1">
                        **kalın**  *italik*  ## Başlık  - Madde listesi
                      </div>
                    </div>

                    <div className="ox-field">
                      <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                        <Paperclip size={11} />
                        Dosya Ekle (Opsiyonel)
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowRevisionFileSelector(true)}
                        className="ox-btn w-full mt-1.5"
                      >
                        <Upload size={13} style={{ marginRight: 6 }} />
                        Dosya Ekle veya Seç
                      </button>
                      {revisionFiles.length > 0 && (
                        <div className="mt-2 p-2 rounded-md" style={{ background: 'var(--good-weak)', border: '1px solid var(--good)' }}>
                          <div className="text-[12px] font-semibold flex items-center gap-1.5 mb-2" style={{ color: 'var(--good)' }}>
                            <CheckCircle size={12} />
                            Seçilen Dosyalar ({revisionFiles.length})
                          </div>
                          <div className="space-y-1">
                            {Array.from(revisionFiles).map((file, idx) => (
                              <div key={idx} className="flex items-center gap-2 p-1.5 rounded text-[12px]" style={{ background: 'var(--surface)' }}>
                                <FileText size={12} style={{ color: 'var(--good)', flexShrink: 0 }} />
                                <span className="flex-1 truncate font-medium">{file.name}</span>
                                <Chip color="var(--good)" bg="var(--good-weak)" mono>
                                  {(file.size / 1024).toFixed(1)} KB
                                </Chip>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ox-field">
                      <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                        <User size={11} />
                        Gönderilecek Kullanıcı <span style={{ color: 'var(--danger)' }}>*</span>
                      </label>
                      <div className="space-y-1.5 mt-1.5 p-2 rounded-md" style={{ background: 'var(--surface-muted)', border: '1px solid var(--border)', maxHeight: 200, overflowY: 'auto' }}>
                        {task && task.assignmentHistory && task.assignmentHistory.length > 0 ? (
                          (() => {
                            const uniqueUsers = new Map();
                            const currentStepIndex = task.currentStepIndex;

                            task.assignmentHistory.forEach((history) => {
                              let isProduction = false;
                              let stepIndex = history.stepIndex;

                              if (workflow && stepIndex !== undefined && stepIndex >= 0 && stepIndex < currentStepIndex && stepIndex < workflow.steps.length) {
                                const step = workflow.steps[stepIndex];
                                const stepType = step.stepType || 'Üretim';
                                if (stepType === 'Üretim') isProduction = true;
                              } else if (!workflow || stepIndex === -1) {
                                const stepType = history.stepType || 'Üretim';
                                if (stepType === 'Üretim') isProduction = true;
                              }

                              if (isProduction) {
                                if (!uniqueUsers.has(history.userId)) {
                                  uniqueUsers.set(history.userId, {
                                    userId: history.userId,
                                    stepNames: [],
                                    stepIndexes: []
                                  });
                                }
                                if (history.stepName) {
                                  uniqueUsers.get(history.userId).stepNames.push(history.stepName);
                                  uniqueUsers.get(history.userId).stepIndexes.push(stepIndex);
                                }
                              }
                            });

                            return Array.from(uniqueUsers.values())
                              .sort((a, b) => Math.min(...a.stepIndexes) - Math.min(...b.stepIndexes))
                              .map((userInfo) => {
                                const historyUser = users.find(u => u.id === userInfo.userId);
                                if (!historyUser) return null;
                                const isSelected = selectedRevisionUsers.includes(userInfo.userId);

                                return (
                                  <label
                                    key={userInfo.userId}
                                    className="flex items-center gap-2 p-2.5 rounded cursor-pointer"
                                    style={{
                                      background: isSelected ? 'var(--warn-weak)' : 'var(--surface)',
                                      border: `1px solid ${isSelected ? 'var(--warn)' : 'var(--border)'}`,
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name="revisionUser"
                                      checked={isSelected}
                                      onChange={() => setSelectedRevisionUsers([userInfo.userId])}
                                      style={{ accentColor: 'var(--warn)' }}
                                    />
                                    <Avatar u={historyUser} size={24} imgSrc={historyUser.picture} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="text-[13px] font-semibold truncate">{historyUser.name}</div>
                                        <Chip color="var(--good)" bg="var(--good-weak)">Üretim</Chip>
                                      </div>
                                      {userInfo.stepNames.length > 0 && (
                                        <div className="text-[11px] ox-text-muted mt-0.5 truncate">
                                          {userInfo.stepNames.join(' → ')}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                );
                              });
                          })()
                        ) : (
                          <div className="text-[12.5px] ox-text-muted text-center py-4">
                            Görev geçmişinde kullanıcı bulunamadı
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                <div className="ox-modal-foot">
                  <button
                    type="button"
                    className="ox-btn"
                    onClick={() => {
                      setShowRevisionDialog(false);
                      setRevisionReason('');
                      setSelectedRevisionUsers([]);
                      setRevisionFiles([]);
                    }}
                  >
                    <X size={13} style={{ marginRight: 6 }} />
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="ox-btn ox-btn-primary"
                    disabled={sendingRevision || !revisionReason.trim() || selectedRevisionUsers.length === 0}
                    onClick={handleSendRevision}
                    style={{ background: 'var(--warn)', borderColor: 'var(--warn)' }}
                  >
                    <RotateCcw size={13} style={{ marginRight: 6 }} />
                    {sendingRevision ? 'Gönderiliyor...' : 'Revizyona Gönder'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Görev Tamamlama Dialog (Direkt Atama için) */}
          {/* Custom Modal for Complete Task - No Dialog Wrapper */}
          {showCompleteDialog && ReactDOM.createPortal(
            <div className="ox-modal-backdrop" style={{ zIndex: 10000 }} onClick={() => {
              setShowCompleteDialog(false);
              setSelectedReviewer('');
            }}>
              <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div className="ox-modal-head">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <CheckCircle size={14} style={{ color: 'var(--good)' }} />
                      Görev Tamamlama Seçenekleri
                    </div>
                    <div className="text-[11.5px] ox-text-muted mt-0.5">
                      {user?.role === 'takim_uyesi'
                        ? 'Kontrol için göndermek istediğiniz kişiyi seçin'
                        : 'Kontrol için gönder veya direkt tamamla'}
                    </div>
                  </div>
                  <button
                    className="ox-btn ox-btn-ghost"
                    onClick={() => {
                      setShowCompleteDialog(false);
                      setSelectedReviewer('');
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="ox-modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                  <div className="space-y-4">
                    <div className="ox-field">
                      <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                        <Users size={11} />
                        {user?.role === 'takim_uyesi' ? 'Kontrol Yapacak Kişi (Zorunlu)' : 'Kontrol Yapacak Kişi (Opsiyonel)'}
                      </label>
                      <div className="space-y-1.5 mt-2">
                        {reviewOptions && reviewOptions.length > 0 ? (
                          reviewOptions.map((option) => {
                            const isSelected = selectedReviewer === option.id;
                            const roleLabel = option.role === 'super_admin' ? 'Süper Admin'
                              : option.role === 'admin' ? 'Admin'
                              : option.role === 'proje_yoneticisi' ? 'Proje Yöneticisi'
                              : option.role === 'takim_lideri' ? 'Takım Lideri' : option.role;
                            return (
                              <div
                                key={option.id}
                                onClick={() => setSelectedReviewer(isSelected ? '' : option.id)}
                                className="flex items-center gap-3 p-2.5 rounded-md cursor-pointer"
                                style={{
                                  background: isSelected ? 'var(--good-weak)' : 'var(--surface-muted)',
                                  border: `1px solid ${isSelected ? 'var(--good)' : 'var(--border)'}`,
                                }}
                              >
                                <div
                                  className="rounded-full flex-shrink-0"
                                  style={{
                                    width: 14,
                                    height: 14,
                                    background: isSelected ? 'var(--good)' : 'transparent',
                                    border: `2px solid ${isSelected ? 'var(--good)' : 'var(--border)'}`,
                                  }}
                                />
                                <Avatar u={option} size={24} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[13px] font-semibold truncate">{option.name}</div>
                                  <div className="text-[11px] ox-text-muted truncate">{roleLabel}</div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-8 px-4 rounded-md" style={{ background: 'var(--warn-weak)', border: '1px solid var(--warn)' }}>
                            <AlertCircle size={32} style={{ color: 'var(--warn)', margin: '0 auto 10px' }} />
                            <div className="text-[13px] font-semibold mb-1">
                              Kontrol için gönderilebilecek yetkili bulunamadı
                            </div>
                            <div className="text-[11.5px] ox-text-muted">
                              {user?.role === 'takim_uyesi'
                                ? 'Takım lideriniz veya proje yöneticiniz sistemde kayıtlı değil. Lütfen yöneticinizle iletişime geçin.'
                                : user?.role === 'takim_lideri'
                                ? 'Bu proje için proje yöneticisi tanımlı değil. Lütfen admin ile iletişime geçin.'
                                : 'Sistemde üst yönetici tanımlı değil.'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedReviewer && (
                      <div className="ox-field">
                        <label className="text-[11.5px] ox-text-muted uppercase tracking-wider flex items-center gap-1.5">
                          <MessageSquare size={11} />
                          Kontrol Açıklaması (İsteğe Bağlı)
                        </label>
                        <textarea
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          placeholder="Kontrol yapacak kişiye göndermek istediğiniz not veya açıklamayı buraya yazabilirsiniz..."
                          className="ox-input w-full mt-1.5"
                          rows={3}
                          style={{ resize: 'none' }}
                        />
                        <div className="text-[11px] ox-text-dim mt-1">
                          **kalın**  *italik*  ## Başlık  - Madde listesi
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="ox-modal-foot">
                  <button
                    type="button"
                    className="ox-btn"
                    onClick={() => {
                      setShowCompleteDialog(false);
                      setSelectedReviewer('');
                    }}
                  >
                    <X size={13} style={{ marginRight: 6 }} />
                    İptal
                  </button>

                  {user?.role !== 'takim_uyesi' && (
                    <button
                      className="ox-btn ox-btn-primary"
                      disabled={!!selectedReviewer}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                          toast.success('Görev tamamlandı');
                          setShowCompleteDialog(false);
                          setSelectedReviewer('');
                          fetchTask();
                        } catch (error) {
                          toast.error('Görev tamamlanamadı');
                        }
                      }}
                      style={{ background: 'var(--good)', borderColor: 'var(--good)' }}
                    >
                      <CheckCircle size={13} style={{ marginRight: 6 }} />
                      Tamamla
                    </button>
                  )}

                  <button
                    className="ox-btn ox-btn-primary"
                    onClick={handleSendForReview}
                    disabled={!selectedReviewer || (reviewOptions.length === 0 && user?.role === 'takim_uyesi')}
                  >
                    <Send size={13} style={{ marginRight: 6 }} />
                    Gönder
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Comments */}
          <div
            ref={commentsRef}
            className="ox-surface rounded-md"
            style={highlightSection === 'comments' ? { boxShadow: '0 0 0 2px var(--accent)' } : {}}
          >
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <MessageSquare size={14} style={{ color: 'var(--accent)' }} />
              <div className="text-[13px] font-semibold">Yorumlar</div>
              <div className="text-[11.5px] ox-text-muted ml-auto">{comments.length}</div>
            </div>
            <div className="p-4 space-y-3">
              {comments.length === 0 ? (
                <div className="text-center ox-text-muted text-[13px] py-6">Henüz yorum yok</div>
              ) : (
                comments.map((comment) => {
                  const isNew = highlightSection === 'comments' && highlightItemId === comment.id;
                  const commentDate = new Date(comment.createdAt);
                  const isRecent = (Date.now() - commentDate.getTime()) < 24 * 60 * 60 * 1000;
                  const isRead = readItems.has(comment.id);
                  const commentUser = users.find(u => u.id === comment.userId);

                  return (
                    <div
                      key={comment.id}
                      id={`comment-${comment.id}`}
                      data-item-id={comment.id}
                      data-item-type="comment"
                      className="p-3 rounded-md"
                      style={{
                        background: isNew ? 'var(--accent-weak)' : 'var(--surface-muted)',
                        border: `1px solid ${isNew ? 'var(--accent)' : 'var(--border)'}`,
                      }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Avatar u={commentUser || { name: getUserName(comment.userId) }} size={22} imgSrc={commentUser?.picture} />
                          <div className="min-w-0 flex-1">
                            <div className="text-[12.5px] font-semibold truncate">{getUserName(comment.userId)}</div>
                            <div className="text-[11px] ox-text-dim">
                              {new Date(comment.createdAt).toLocaleDateString('tr-TR')} • {new Date(comment.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              {comment.editedAt && <span className="ml-1 italic">(düzenlendi)</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {(isNew || (isRecent && !isRead)) && (
                            <Chip color="var(--danger)" bg="var(--danger-weak)">YENİ</Chip>
                          )}
                          {canEditComment(comment) && (
                            <>
                              <button
                                data-testid={`comment-edit-${comment.id}`}
                                onClick={() => { setEditingCommentId(comment.id); setEditingCommentContent(comment.content); }}
                                className="ox-btn ox-btn-ghost"
                                style={{ padding: '3px 5px' }}
                                title="Yorumu düzenle"
                              >
                                <Edit2 size={11} />
                              </button>
                              <button
                                data-testid={`comment-delete-${comment.id}`}
                                onClick={() => handleDeleteComment(comment.id)}
                                className="ox-btn ox-btn-ghost"
                                style={{ padding: '3px 5px', color: 'var(--danger)' }}
                                title="Yorumu sil"
                              >
                                <Trash2 size={11} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingCommentContent}
                            onChange={(e) => setEditingCommentContent(e.target.value)}
                            className="ox-input w-full"
                            rows={3}
                            style={{ resize: 'vertical' }}
                          />
                          <div className="flex gap-2">
                            <button onClick={() => handleEditComment(comment.id)} className="ox-btn ox-btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>
                              <Save size={11} style={{ marginRight: 4 }} />
                              Kaydet
                            </button>
                            <button onClick={() => { setEditingCommentId(null); setEditingCommentContent(''); }} className="ox-btn" style={{ padding: '4px 10px', fontSize: 12 }}>
                              <X size={11} style={{ marginRight: 4 }} />
                              İptal
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[13px]" dangerouslySetInnerHTML={formatComment(comment.content)} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {(user?.permissions?.includes('all') || user?.permissions?.includes('can_comment')) && (
              <div className="p-4" style={{ borderTop: '1px solid var(--border)' }}>
                <form onSubmit={handleAddComment} className="space-y-2">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Yorum ekleyin..."
                    rows={4}
                    data-testid="comment-input"
                    className="ox-input w-full"
                    style={{ resize: 'vertical' }}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] ox-text-dim">
                      **kalın**  *italik*  ## Başlık  - Madde listesi
                    </div>
                    <button type="submit" className="ox-btn ox-btn-primary" data-testid="submit-comment-button">
                      <Send size={13} style={{ marginRight: 6 }} />
                      Yorum Ekle
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Action Buttons - Checkout/Revision/Complete */}
          {/* Checkout Button - Workflow görevleri için */}
          {((task.assignedTo === user.id || isAdmin) &&
            task.status?.toLowerCase() !== 'completed' &&
            task.status?.toLowerCase() !== 'tamamlandı' &&
            task.workflowId &&
            task.workflowId !== 'none' &&
            task.workflowId !== '') && (
            <div className="ox-surface rounded-md p-4">
              <div className="flex flex-col gap-2">
                {task.currentRevision ? (
                  <>
                    {canResolveRevision() && (
                      <button
                        onClick={() => setConfirmAction({
                          type: 'resolve_revision',
                          title: 'Revizyon Tamamlama',
                          message: 'Revizyonu tamamlamak istediğinizden emin misiniz? Görev gönderene geri dönecektir.',
                          onConfirm: handleResolveRevision
                        })}
                        className="ox-btn ox-btn-primary w-full"
                        style={{ padding: '10px 16px', fontSize: 14, background: 'var(--review)', borderColor: 'var(--review)' }}
                      >
                        <CheckCircle size={16} style={{ marginRight: 8 }} />
                        Revizyon Tamamlandı
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setConfirmAction({
                        type: 'checkout',
                        title: 'Adımı Tamamla',
                        message: 'Bu adımı tamamlayıp sonraki adıma geçmek istediğinizden emin misiniz?',
                        onConfirm: handleCheckoutClick
                      })}
                      className="ox-btn ox-btn-primary w-full"
                      style={{ padding: '10px 16px', fontSize: 14 }}
                      data-testid="checkout-button"
                    >
                      <CheckCircle size={16} style={{ marginRight: 8 }} />
                      Tamamla ve Sonraki Adıma Geç
                    </button>
                    {canSendRevision() && (
                      <button
                        onClick={() => setShowRevisionDialog(true)}
                        className="ox-btn w-full"
                        style={{ padding: '10px 16px', fontSize: 14, background: 'var(--warn)', borderColor: 'var(--warn)', color: '#fff' }}
                      >
                        <RotateCcw size={16} style={{ marginRight: 8 }} />
                        Revizyona Gönder
                      </button>
                    )}
                  </>
                )}
              </div>
              <p className="text-[11.5px] ox-text-muted text-center mt-3">
                {task.currentRevision
                  ? 'Revizyon tamamlandıktan sonra görev gönderene geri dönecektir'
                  : (task.assignedTo === user.id
                    ? 'Bu adımı tamamlayıp sonraki kişiye/adıma göndereceksiniz'
                    : '(Admin yetkisi ile) Bu adımı tamamlayıp sonraki kişiye/adıma göndereceksiniz')
                }
              </p>
            </div>
          )}

          {/* Admin Revizyona Gönder - Tamamlanmış iş akışlı görevler için */}
          {isAdmin &&
            (task.status?.toLowerCase() === 'completed' || task.status?.toLowerCase() === 'tamamlandı') &&
            task.workflowId &&
            task.workflowId !== 'none' &&
            task.workflowId !== '' &&
            !task.currentRevision &&
            canSendRevision() && (
            <div className="ox-surface rounded-md p-4">
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setShowRevisionDialog(true)}
                  className="ox-btn w-full"
                  style={{ padding: '10px 16px', fontSize: 14, background: 'var(--warn)', borderColor: 'var(--warn)', color: '#fff' }}
                >
                  <RotateCcw size={16} style={{ marginRight: 8 }} />
                  Revizyona Gönder
                </button>
              </div>
              <p className="text-[11.5px] ox-text-muted text-center mt-3">
                Bu görev tamamlanmış. Sadece revizyona gönderebilirsiniz.
              </p>
            </div>
          )}

          {/* Manuel Atama - Complete/Review Buttons */}
          {task.assignedTo === user.id &&
            task.status !== 'completed' &&
            (!task.workflowId || task.workflowId === 'none' || task.workflowId === '') && (
            <div className="ox-surface rounded-md p-4">
              <div className="flex flex-col gap-2">
                {task.currentRevision ? (
                  <>
                    {canResolveRevision() && (
                      <button
                        onClick={() => setConfirmAction({
                          type: 'resolve_revision',
                          title: 'Revizyon Tamamlama',
                          message: 'Revizyonu tamamlamak istediğinizden emin misiniz? Görev gönderene geri dönecektir.',
                          onConfirm: handleResolveRevision
                        })}
                        className="ox-btn ox-btn-primary w-full"
                        style={{ padding: '10px 16px', fontSize: 14, background: 'var(--review)', borderColor: 'var(--review)' }}
                      >
                        <CheckCircle size={16} style={{ marginRight: 8 }} />
                        Revizyon Tamamlandı
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {task.status !== 'completed' && task.status !== 'tamamlandı' && (
                      <button
                        onClick={() => {
                          if (isAdmin) {
                            setConfirmAction({
                              type: 'complete_task',
                              title: 'Görevi Tamamla',
                              message: 'Bu görevi tamamlamak istediğinizden emin misiniz?',
                              onConfirm: async () => {
                                try {
                                  await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                                  toast.success('Görev tamamlandı');
                                  fetchTask();
                                } catch (error) {
                                  toast.error('Görev tamamlanamadı');
                                }
                              }
                            });
                            return;
                          }
                          if (!task.workflowId) {
                            prepareReviewOptions();
                            setShowCompleteDialog(true);
                          } else {
                            setConfirmAction({
                              type: 'complete_task',
                              title: 'Görevi Tamamla',
                              message: 'Bu görevi tamamlamak istediğinizden emin misiniz?',
                              onConfirm: async () => {
                                try {
                                  await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                                  toast.success('Görev tamamlandı');
                                  fetchTask();
                                } catch (error) {
                                  toast.error('Görev tamamlanamadı');
                                }
                              }
                            });
                          }
                        }}
                        className="ox-btn w-full"
                        style={{ padding: '10px 16px', fontSize: 14, background: 'var(--good)', borderColor: 'var(--good)', color: '#fff' }}
                        data-testid="complete-button"
                      >
                        <CheckCircle size={16} style={{ marginRight: 8 }} />
                        Görevi Tamamla
                      </button>
                    )}
                    {canSendRevision() && (
                      <button
                        onClick={() => setShowRevisionDialog(true)}
                        className="ox-btn w-full"
                        style={{ padding: '10px 16px', fontSize: 14, background: 'var(--warn)', borderColor: 'var(--warn)', color: '#fff' }}
                      >
                        <RotateCcw size={16} style={{ marginRight: 8 }} />
                        Revizyona Gönder
                      </button>
                    )}
                  </>
                )}
              </div>
              <p className="text-[11.5px] ox-text-muted text-center mt-3">
                {task.currentRevision
                  ? 'Revizyon tamamlandıktan sonra görev gönderene geri dönecektir'
                  : (task.status === 'completed' || task.status === 'tamamlandı'
                    ? 'Bu görev tamamlanmış. Sadece revizyona gönderebilirsiniz.'
                    : (task.assignedTo === user.id
                      ? 'Görevi tamamlayabilir veya inceleme için gönderebilirsiniz'
                      : '(Admin yetkisi ile) Görevi tamamlandı olarak işaretleyeceksiniz')
                  )
                }
              </p>
            </div>
          )}

          {/* Files */}
          <div
            ref={filesRef}
            className="ox-surface rounded-md"
            style={highlightSection === 'files' ? { boxShadow: '0 0 0 2px var(--accent)' } : {}}
          >
            <div className="px-4 py-2.5 flex items-center justify-between gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip size={14} style={{ color: 'var(--accent)' }} />
                <div className="text-[13px] font-semibold">Dosyalar</div>
                {fileSummary && <div className="text-[11.5px] ox-text-muted">({fileSummary})</div>}
              </div>
              {(user?.permissions?.includes('all') || user?.permissions?.includes('can_upload_files')) && (
                <button
                  disabled={uploading}
                  onClick={() => setShowFileSelector(true)}
                  className="ox-btn"
                  data-testid="add-file-button"
                >
                  <Upload size={12} style={{ marginRight: 5 }} />
                  {uploading ? 'Yükleniyor...' : 'Dosya Ekle'}
                </button>
              )}
            </div>

            <div className="p-4">
              <div className="text-[11.5px] p-2 rounded mb-3" style={{ background: 'var(--accent-weak)', color: 'var(--accent)' }}>
                <strong>Maksimum dosya boyutu:</strong> {project?.max_file_size_mb || 50} MB
              </div>

              {uploading && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] ox-text-muted">Yükleniyor...</span>
                    <span className="text-[12px] font-semibold ox-mono" style={{ color: 'var(--accent)' }}>{uploadProgress}%</span>
                  </div>
                  <div className="ox-track">
                    <div style={{ width: `${uploadProgress}%`, height: '100%', background: 'var(--accent)' }} />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                {files.length === 0 ? (
                  <div className="text-center ox-text-muted text-[13px] py-8">Henüz dosya yok</div>
                ) : (
                  files.map((file) => {
                    const isNew = highlightSection === 'files' && highlightItemId === file.id;
                    const fileDate = new Date(file.createdAt);
                    const isRecent = (Date.now() - fileDate.getTime()) < 24 * 60 * 60 * 1000;
                    const isRead = readItems.has(file.id);

                    return (
                      <div
                        key={file.id}
                        id={`file-${file.id}`}
                        data-item-id={file.id}
                        data-item-type="file"
                        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-2.5 rounded-md"
                        style={{
                          background: isNew ? 'var(--accent-weak)' : 'var(--surface-muted)',
                          border: `1px solid ${isNew ? 'var(--accent)' : 'var(--border)'}`,
                        }}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 overflow-hidden">
                          {(() => {
                            const { Icon: FileIcon, color } = getFileIcon(file.filename);
                            const extension = getFileExtension(file.filename);
                            return (
                              <div className="relative flex-shrink-0">
                                <div className="flex items-center justify-center rounded" style={{ width: 36, height: 36, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                  <FileIcon className={color} style={{ width: 18, height: 18 }} />
                                </div>
                                {extension && (
                                  <div className="absolute -bottom-1 -right-1 ox-mono" style={{ fontSize: 7, fontWeight: 700, padding: '1px 3px', borderRadius: 3, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                                    {extension.toUpperCase()}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[12.5px] font-medium truncate max-w-[150px] sm:max-w-[220px]" title={file.filename}>{file.filename}</p>
                              {(isNew || (isRecent && !isRead)) && (
                                <Chip color="var(--danger)" bg="var(--danger-weak)">YENİ</Chip>
                              )}
                            </div>
                            <p className="text-[11px] ox-text-dim truncate">
                              {getUserName(file.uploadedBy)} • {new Date(file.createdAt).toLocaleDateString('tr-TR')}
                              {file.fileSize && ` • ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => {
                              const downloadUrl = `${API}/files/${file.id}/download?token=${localStorage.getItem('session_token') || ''}`;
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = file.filename || '';
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Dosya indiriliyor');
                            }}
                            className="ox-btn ox-btn-ghost"
                            style={{ padding: '5px 7px', color: 'var(--accent)' }}
                            title="İndir"
                          >
                            <Download size={13} />
                          </button>
                          {(isAdmin || user?.permissions?.includes('all') || user?.permissions?.includes('can_delete_files')) && (
                            <button
                              onClick={() => handleDeleteFile(file.id)}
                              className="ox-btn ox-btn-ghost"
                              style={{ padding: '5px 7px', color: 'var(--danger)' }}
                              title="Sil"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Task Dialog */}
      {showEditDialog && (
        <div className="ox-modal-backdrop" onClick={() => setShowEditDialog(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Edit size={14} style={{ color: 'var(--accent)' }} />
                Görevi Düzenle
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowEditDialog(false)}>
                <X size={14} />
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="ox-modal-body space-y-4">
                <div className="ox-field">
                  <label htmlFor="edit-title" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                    Görev Adı <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    id="edit-title"
                    className="ox-input w-full mt-1"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="ox-field">
                  <label htmlFor="edit-description" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                    Açıklama
                  </label>
                  <textarea
                    id="edit-description"
                    className="ox-input w-full mt-1"
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={4}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn" onClick={() => setShowEditDialog(false)}>
                  <X size={13} style={{ marginRight: 6 }} />
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  <Save size={13} style={{ marginRight: 6 }} />
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Task Dialog */}
      {showDeleteDialog && (
        <div className="ox-modal-backdrop" onClick={() => !deleting && setShowDeleteDialog(false)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-sm font-semibold flex items-center gap-2">
                <AlertCircle size={14} style={{ color: 'var(--danger)' }} />
                Görevi Sil
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => !deleting && setShowDeleteDialog(false)}>
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <div className="text-[13px] ox-text-muted">
                Bu görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve görevle ilgili tüm yorumlar, dosyalar ve geçmiş bilgileri silinecektir.
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
                <X size={13} style={{ marginRight: 6 }} />
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={handleDeleteTask} disabled={deleting}>
                <Trash2 size={13} style={{ marginRight: 6 }} />
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Dialog - Atama değişikliği (Sadece Admin) */}
      {showReassignDialog && (
        <div className="ox-modal-backdrop" onClick={() => !reassigning && setShowReassignDialog(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <UserPlus size={14} style={{ color: 'var(--accent)' }} />
                  {task?.workflowId ? 'Adım Atamasını Değiştir' : 'Görev Atamasını Değiştir'}
                </div>
                <div className="text-[11.5px] ox-text-muted mt-0.5">
                  {task?.workflowId
                    ? 'Mevcut adımı başka bir kullanıcıya atayın. Eski kullanıcı fokusundan görev çıkacaktır.'
                    : 'Görevi başka bir kullanıcıya atayın. Eski kullanıcı fokusundan görev çıkacaktır.'}
                </div>
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => !reassigning && setShowReassignDialog(false)}>
                <X size={14} />
              </button>
            </div>

            <div className="ox-modal-body space-y-4">
              <div className="ox-surface-muted rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wider ox-text-dim mb-1">Mevcut Atanan</div>
                <div className="text-[13px] font-medium">
                  {reassignData.oldUserName || users.find(u => u.id === task?.assignedTo)?.name || 'Bilinmeyen'}
                </div>
              </div>

              <div className="ox-field">
                <label htmlFor="reassign-user" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  Yeni Kullanıcı <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <select
                  id="reassign-user"
                  className="ox-select w-full mt-1"
                  value={reassignData.newUserId}
                  onChange={(e) => setReassignData({ ...reassignData, newUserId: e.target.value })}
                >
                  <option value="">Kullanıcı seçin...</option>
                  {availableUsers.filter(u => u.status !== 'inactive' && u.id !== reassignData.oldUserId).map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} - {u.role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ox-field">
                <label htmlFor="reassign-reason" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  Neden (Opsiyonel)
                </label>
                <textarea
                  id="reassign-reason"
                  className="ox-input w-full mt-1"
                  value={reassignData.reason}
                  onChange={(e) => setReassignData({ ...reassignData, reason: e.target.value })}
                  placeholder="Atama değişikliği nedenini belirtebilirsiniz..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>

            <div className="ox-modal-foot">
              <button
                type="button"
                className="ox-btn"
                onClick={() => {
                  setShowReassignDialog(false);
                  setReassignData({ newUserId: '', reason: '', assignmentIndex: null, oldUserId: null, oldUserName: null });
                }}
                disabled={reassigning}
              >
                <X size={13} style={{ marginRight: 6 }} />
                İptal
              </button>
              <button
                type="button"
                className="ox-btn ox-btn-primary"
                onClick={handleReassign}
                disabled={reassigning || !reassignData.newUserId}
              >
                <UserPlus size={13} style={{ marginRight: 6 }} />
                {reassigning ? 'Değiştiriliyor...' : 'Atamayı Değiştir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quantity Dialog - Takım üyesi için miktar düzenleme */}
      {showEditQuantityDialog && (
        <div className="ox-modal-backdrop" onClick={() => setShowEditQuantityDialog(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <form onSubmit={handleSaveQuantity}>
              <div className="ox-modal-head">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Edit2 size={14} style={{ color: 'var(--accent)' }} />
                    Miktar Değerini Düzenle
                  </div>
                  <div className="text-[11.5px] ox-text-muted mt-0.5">
                    Sadece miktar değerini güncelleyebilirsiniz.
                  </div>
                </div>
                <button type="button" className="ox-btn ox-btn-ghost" onClick={() => setShowEditQuantityDialog(false)}>
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body">
                <div className="ox-field">
                  <label htmlFor="edit-quantity-value" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                    Miktar Değeri {task?.quantityType === 'süre' ? '(SS:DD:SS)' : '(Adet)'}
                  </label>
                  <input
                    id="edit-quantity-value"
                    type="text"
                    className="ox-input w-full mt-1"
                    value={editQuantityValue}
                    onChange={(e) => setEditQuantityValue(e.target.value)}
                    placeholder={task?.quantityType === 'süre' ? '02:30:00' : '10'}
                    required
                  />
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn" onClick={() => setShowEditQuantityDialog(false)}>
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  <Save size={13} style={{ marginRight: 6 }} />
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Selector Modal */}
      {showFileSelector && ReactDOM.createPortal(
        <div className="ox-modal-backdrop" style={{ zIndex: 10000 }} onClick={() => setShowFileSelector(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '85vh', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <button
              onClick={() => setShowFileSelector(false)}
              className="ox-btn ox-btn-ghost"
              style={{ position: 'absolute', right: 10, top: 10, zIndex: 10001, padding: '6px' }}
            >
              <X size={14} />
            </button>

            <FileSelector
              onFileSelect={handleAttachExistingFile}
              onNewFileUpload={(file) => {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                if (fileInputRef.current) {
                  fileInputRef.current.files = dataTransfer.files;
                  fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }}
              onClose={() => setShowFileSelector(false)}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Revision File Selector Modal */}
      {showRevisionFileSelector && ReactDOM.createPortal(
        <div className="ox-modal-backdrop" style={{ zIndex: 10000 }} onClick={() => setShowRevisionFileSelector(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '85vh', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <button
              onClick={() => setShowRevisionFileSelector(false)}
              className="ox-btn ox-btn-ghost"
              style={{ position: 'absolute', right: 10, top: 10, zIndex: 10001, padding: '6px' }}
            >
              <X size={14} />
            </button>

            <FileSelector
              onFileSelect={(file) => {
                const simulatedFile = new File([], file.filename, { type: file.fileType || 'application/octet-stream' });
                simulatedFile.isExisting = true;
                simulatedFile.existingFileId = file.id;
                setRevisionFiles(prev => [...prev, simulatedFile]);
                setShowRevisionFileSelector(false);
                toast.success('Dosya eklendi');
              }}
              onNewFileUpload={(file) => {
                setRevisionFiles(prev => [...prev, file]);
                setShowRevisionFileSelector(false);
              }}
              onClose={() => setShowRevisionFileSelector(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Adım Miktar Düzenleme Modal */}
      {showEditStepQuantityDialog && (
        <div className="ox-modal-backdrop" onClick={() => setShowEditStepQuantityDialog(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="ox-modal-head">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Edit2 size={14} style={{ color: 'var(--accent)' }} />
                  Adım Miktarını Düzenle
                </div>
                <div className="text-[11.5px] ox-text-muted mt-0.5">
                  Bu adımın etiket ve miktar bilgilerini güncelleyin
                </div>
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowEditStepQuantityDialog(false)}>
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body space-y-4">
              <div className="ox-field">
                <label htmlFor="edit-step-tag" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  Etiket
                </label>
                <select
                  id="edit-step-tag"
                  className="ox-select w-full mt-1"
                  value={editStepQuantityData.tagId}
                  onChange={(e) => setEditStepQuantityData(prev => ({ ...prev, tagId: e.target.value }))}
                >
                  <option value="">Etiket seçin</option>
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                  ))}
                </select>
              </div>

              <div className="ox-field">
                <label htmlFor="edit-step-quantity-type" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  Miktar Tipi
                </label>
                <select
                  id="edit-step-quantity-type"
                  className="ox-select w-full mt-1"
                  value={editStepQuantityData.quantityType}
                  onChange={(e) => setEditStepQuantityData(prev => ({ ...prev, quantityType: e.target.value }))}
                >
                  <option value="adet">Adet</option>
                  <option value="süre">Süre</option>
                </select>
              </div>

              <div className="ox-field">
                <label htmlFor="edit-step-quantity-value" className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  {editStepQuantityData.quantityType === 'süre' ? 'Süre (SS:DD:SS)' : 'Miktar Değeri'}
                </label>
                <input
                  id="edit-step-quantity-value"
                  type="text"
                  className="ox-input w-full mt-1"
                  placeholder={editStepQuantityData.quantityType === 'süre' ? '02:30:00' : '10'}
                  value={editStepQuantityData.quantityValue}
                  onChange={(e) => setEditStepQuantityData(prev => ({ ...prev, quantityValue: e.target.value }))}
                  autoFocus
                />
              </div>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setShowEditStepQuantityDialog(false)}>
                İptal
              </button>
              <button className="ox-btn ox-btn-primary" onClick={handleUpdateStepQuantity}>
                <Save size={13} style={{ marginRight: 6 }} />
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input 
        ref={fileInputRef}
        type="file" 
        className="hidden" 
        onChange={handleFileUpload} 
        disabled={uploading} 
      />

      {/* Paylaşım Modalı */}
      {showShareModal && (
        <div className="ox-modal-backdrop" style={{ zIndex: 10001 }} onClick={() => setShowShareModal(false)}>
          <div
            className="ox-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }}
            data-testid="share-modal"
          >
            <div className="ox-modal-head">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Share2 size={14} style={{ color: 'var(--accent)' }} />
                Görevi Paylaş
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => setShowShareModal(false)}>
                <X size={14} />
              </button>
            </div>

            <div className="p-4 space-y-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="ox-field">
                <label className="text-[11.5px] ox-text-muted uppercase tracking-wider">
                  Paylaşım Notu <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <textarea
                  data-testid="share-note-input"
                  placeholder="Paylaşım notunuzu yazın..."
                  value={shareNote}
                  onChange={(e) => setShareNote(e.target.value)}
                  rows={2}
                  className="ox-input w-full mt-1"
                  style={{ resize: 'vertical', minHeight: 60 }}
                />
              </div>
              <input
                data-testid="share-search-input"
                type="text"
                placeholder="Kullanıcı ara..."
                value={shareSearchQuery}
                onChange={(e) => setShareSearchQuery(e.target.value)}
                className="ox-input w-full"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: 400 }}>
              {shareUsers
                .filter(u => {
                  const q = shareSearchQuery.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                })
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'))
                .map(u => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-2 rounded ox-row-hover"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar u={u} size={32} />
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium truncate">{u.name}</div>
                        <div className="text-[11px] ox-text-muted truncate">{u.email}</div>
                      </div>
                    </div>
                    <button
                      data-testid={`share-user-${u.id}`}
                      onClick={() => handleShareTask(u.id)}
                      disabled={sharingUserId === u.id}
                      className="ox-btn ox-btn-primary flex-shrink-0"
                      style={{ padding: '5px 10px', fontSize: 12 }}
                    >
                      {sharingUserId === u.id ? 'Gönderiliyor...' : 'Paylaş'}
                    </button>
                  </div>
                ))
              }
              {shareUsers.filter(u => {
                const q = shareSearchQuery.toLowerCase();
                return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
              }).length === 0 && (
                <div className="text-center text-sm ox-text-muted py-8">Kullanıcı bulunamadı</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onay Modalı */}
      {confirmAction && ReactDOM.createPortal(
        <div className="ox-modal-backdrop" style={{ zIndex: 10001 }} onClick={() => setConfirmAction(null)}>
          <div
            className="ox-modal ox-modal-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-action-modal"
          >
            <div className="ox-modal-head">
              <div className="text-sm font-semibold flex-1">{confirmAction.title}</div>
              <button className="ox-btn ox-btn-ghost ml-auto" onClick={() => setConfirmAction(null)}>
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <div className="text-sm ox-text-muted">{confirmAction.message}</div>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn"
                onClick={() => setConfirmAction(null)}
                data-testid="confirm-cancel-btn"
              >
                İptal
              </button>
              <button
                className="ox-btn ox-btn-primary"
                onClick={async () => {
                  const fn = confirmAction.onConfirm;
                  setConfirmAction(null);
                  await fn();
                }}
                data-testid="confirm-ok-btn"
              >
                <CheckCircle size={13} style={{ marginRight: 6 }} />
                Tamam
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
